/**
 * Custom Box Preview Modal Component
 * Full-screen drawer for viewing and editing custom box selections
 * Styled to match cart-drawer.liquid
 */

// Portal-variant detection — canonical copy lives in custom-box-status-bar.js
// (which loads first via defer order); this guarded duplicate keeps the modal
// working if that script ever fails or gets reordered.
if (!window.wgPortalThemeMode) {
  window.wgPortalThemeMode = function () {
    try {
      var qp = new URLSearchParams(window.location.search).get('wg_portal');
      if (qp === '1' || qp === 'portal') {
        localStorage.removeItem('wg_builder_portal');
      } else if (qp === '0' || qp === 'off') {
        localStorage.setItem('wg_builder_portal', 'control');
      }
    } catch (e) { /* private mode */ }
    // wg_builder_portal is the single opt-out key, deliberately: the old
    // wg_portal_assignment key still holds a stale 'control' for everyone the
    // experiment bucketed that way, and reading it would strand them on the
    // pre-portal chrome forever.
    try {
      if (localStorage.getItem('wg_builder_portal') === 'control') return false;
    } catch (e) { /* private mode */ }
    return true;
  };
}

// Same look as add-to-cart overlay (class + styles); distinct id so we never remove ATC’s layer mid-flow.
const WG_PREVIEW_BLOCKING_OVERLAY_ID = 'wildgrain-preview-modal-blocking-overlay';

function showWildgrainUiBlockingOverlay() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(WG_PREVIEW_BLOCKING_OVERLAY_ID)) return;
  const el = document.createElement('div');
  el.id = WG_PREVIEW_BLOCKING_OVERLAY_ID;
  el.className = 'wildgrain-add-to-cart-blocking-overlay';
  el.setAttribute('role', 'presentation');
  el.setAttribute('aria-hidden', 'true');
  el.style.cssText = [
    'position:fixed',
    'inset:0',
    'box-sizing:border-box',
    'margin:0',
    'padding:0',
    'width:100vw',
    'height:100vh',
    'background:rgba(15,23,42,0.22)',
    'pointer-events:auto',
    'cursor:wait',
    'touch-action:none',
    '-webkit-tap-highlight-color:transparent',
    'opacity:0',
    'transition:opacity 0.2s ease-out'
  ].join(';');
  el.style.setProperty('z-index', '2147483646', 'important');
  document.body.appendChild(el);
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (el.isConnected) el.style.opacity = '1';
    });
  });
}

function hideWildgrainUiBlockingOverlay() {
  if (typeof document === 'undefined') return;
  const el = document.getElementById(WG_PREVIEW_BLOCKING_OVERLAY_ID);
  if (el) el.remove();
}

/** Same trash / minus icons as wildgrain bundle ProductCard (qty 1 → remove) */
const WG_BUNDLE_MINUS_SVG = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M20 12H4"/></svg>`;
const WG_BUNDLE_TRASH_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>`;
const WG_BUNDLE_IMAGE_PLACEHOLDER = `<div class="custom-box-preview-modal__thumb-placeholder" aria-hidden="true"></div>`;

/** Green check for “Box complete” status line in preview modal header */
const WG_STATUS_CHECK_SVG = `<svg class="custom-box-preview-modal__status-check-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

/** Synthesized free-item entry for the hot-dog-bun landing-page promo. Not in any plan catalog; the cart-transform function zeros its price at cart time. First-box only — renewals don't include it. */
const WG_HOTDOG_PROMO_FREE_ITEM = {
  id: '7428576968771',
  title: 'Slow-Fermented Hot Dog Buns (8-pack)',
  image: 'https://cdn.shopify.com/s/files/1/0078/2761/9887/files/Wildgrain_Slow_Fermented_Hot_Dog_Buns.jpg?v=1762459187',
  variants: [{ price: '10.00' }],
  freeSubtitle: 'Free in your first box · Save $10.00',
};

/** Free items that can be the box's comped item without being in the plan's own catalog:
 *  the ?free-item= campaign override (freeItemSwap.js in wildgrain-bundle) and the vegan
 *  swap. The builder writes the chosen product's id into boxState.freeProduct, but this
 *  modal can only look it up among the recipes it loaded for the selected plan — so these
 *  stand in when the lookup misses. */
const WG_FREE_ITEM_FALLBACKS = {
  '6824966193219': {
    id: '6824966193219',
    title: 'Slow-Churned French Butter (Salted)',
    image: 'https://cdn.shopify.com/s/files/1/0078/2761/9887/files/PXL_20231127_183716638.PORTRAIT.ORIGINAL_2_grande_0396e454-5641-4d1b-bb2f-6fcbecad1962.jpg?v=1776183664',
  },
  '7098150223939': {
    id: '7098150223939',
    title: 'Vegan Croissants (4-pack)',
    image: 'https://cdn.shopify.com/s/files/1/0078/2761/9887/files/PlantbasedCroissants.jpg?v=1762458694',
  },
};

class CustomBoxPreviewModal extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.boxState = null;
    this.products = [];
    this.cartAddons = [];
    this.hasBoxInCart = false;
    this.overlay = null;
    this.modalContent = null;
    this.closeButton = null;
    this.productsGrid = null;
    // Box cart line metadata (populated when box is in cart) — used for the
    // read-only delivery-frequency info pill in the modal header.
    this.boxCartLine = null;
  }

  connectedCallback() {
    // Portal visitors (see wgPortalThemeMode in custom-box-status-bar.js —
    // defined there since both assets load together) get the portal-styled
    // drawer: "Your Box" header, coral/cream look, and CTAs that route to the
    // box builder instead of adding to cart (extras now live in the app; the
    // Member Deals slider is retired under portal).
    this.isPortalMode = !!(window.wgPortalThemeMode && window.wgPortalThemeMode());
    if (this.isPortalMode) this.classList.add('portal-mode');
    // Portal Beam treatment (ported from the app's BeamDonation): collapsed
    // one-line pitch that expands into the widget on demand. Selection events
    // bubble composed across the widget's shadow root, so window hears them.
    this.beamOpen = false;
    this.prevBoxFullForBeam = null;
    if (this.isPortalMode) {
      window.addEventListener('beamnonprofitselect', () => this.syncPortalBeam());
      window.addEventListener('beamnonprofitselectionremoved', () => this.syncPortalBeam());
    }
    this.render();
    if (this.isPortalMode) {
      const heading = this.querySelector('.drawer__heading');
      if (heading) {
        heading.textContent = 'Your Box';
        // Global .idler rules (display font, coral, uppercase) outrank the
        // portal CSS overrides — drop the class so the portal heading style
        // (ink, sentence case, body font) applies.
        heading.classList.remove('idler');
      }
      // "Plan settings" (gear) beside the close button, same as the app's
      // tray header. Plan/size/frequency are chosen in the builder, so it
      // routes to the bundle PDP with the current selection.
      const headerBar = this.querySelector('.drawer__header');
      if (headerBar && !headerBar.querySelector('[data-wg-plan-settings]')) {
        const planBtn = document.createElement('button');
        planBtn.type = 'button';
        planBtn.className = 'wg-plan-settings';
        planBtn.setAttribute('data-wg-plan-settings', '');
        planBtn.innerHTML = `
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.28z"/>
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
          </svg>
          Plan settings`;
        planBtn.addEventListener('click', () => {
          window.location.href = this.buildBundleCustomizeUrl();
        });
        headerBar.appendChild(planBtn);
      }
    }
    this.attachEventListeners();
    this.setupBoxReadyOverlayListener();
    this.setupExternalQuantityChangeListeners();
  }

  setupBoxReadyOverlayListener() {
    // Listen for box-ready-overlay:displayed event to automatically open modal
    // Only auto-open on recharge-bundle pages
    this.boxReadyOverlayHandler = () => {
      // Check if gifting drawer is currently open - don't auto-open if it is
      const giftingDrawer = document.querySelector('gifting-drawer');
      const isGiftingDrawerOpen = giftingDrawer && giftingDrawer.hasAttribute('active');

      // Don't auto-open if gifting drawer is open
      if (isGiftingDrawerOpen) {
        return;
      }

      // Check if we're on a page using the recharge-bundle template
      const isRechargeBundlePage = document.body.classList.contains('recharge-bundle') ||
                                    document.querySelector('.recharge-bundles-section') !== null ||
                                    document.querySelector('[data-section-type="recharge-bundles"]') !== null;

      // Only auto-open if on recharge-bundle page
      if (isRechargeBundlePage) {
        this.open();
      }
    };
    window.addEventListener('box-ready-overlay:displayed', this.boxReadyOverlayHandler);
  }

  disconnectedCallback() {
    // Clean up event listener when component is removed
    if (this.boxReadyOverlayHandler) {
      window.removeEventListener('box-ready-overlay:displayed', this.boxReadyOverlayHandler);
    }
    if (this.externalQuantityChangeHandler) {
      window.removeEventListener('custom-box:quantity-change-request', this.externalQuantityChangeHandler);
    }
    if (this.externalRemoveHandler) {
      window.removeEventListener('custom-box:remove-request', this.externalRemoveHandler);
    }
  }

  setupExternalQuantityChangeListeners() {
    // Listen for quantity change requests from other components
    this.externalQuantityChangeHandler = (event) => {
      const { productId, action } = event.detail;
      if (productId && action) {
        this.handleQuantityChange(productId, action);
      }
    };

    this.externalRemoveHandler = (event) => {
      const { productId } = event.detail;
      if (productId) {
        this.handleRemove(productId);
      }
    };

    window.addEventListener('custom-box:quantity-change-request', this.externalQuantityChangeHandler);
    window.addEventListener('custom-box:remove-request', this.externalRemoveHandler);
  }

  async open(forceOpen = false) {
    const giftingDrawer = document.querySelector('gifting-drawer');
    const isGiftingDrawerOpen = giftingDrawer && giftingDrawer.hasAttribute('active');

    if (!forceOpen && isGiftingDrawerOpen) {
      return;
    }

    if (forceOpen && isGiftingDrawerOpen && giftingDrawer) {
      giftingDrawer.close();
    }

    this.closeOtherDrawers();

    this.isOpen = true;
    this.removeAttribute('hidden');
    this.removeAttribute('inert');

    // Show loading spinner in the drawer body immediately
    if (this.productsGrid) {
      this.productsGrid.innerHTML = `
        <div class="custom-box-preview-modal__body-loading">
          <span class="custom-box-preview-modal__body-spinner" aria-label="Loading box contents"></span>
        </div>
      `;
    }

    // Lock body scroll
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('custom-box-preview-modal-open');
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    document.documentElement.style.overflow = 'hidden';

    // Slide the drawer in on the next frame
    requestAnimationFrame(() => {
      this.classList.add('active');
      this.setAttribute('active', '');
    });

    // Load data in the background while the drawer is already visible
    try {
      await this.loadBoxState();

      // A shopper can get here with no saved box at all: in portal mode the
      // Cart pill also shows for cart extras alone (e.g. a gift item added
      // from a gifting PDP). renderProducts() bails on a null boxState and
      // would leave the loading spinner up forever — synthesize the same
      // empty state the status bar's checkVisibility() uses so the tray
      // renders empty slots plus the cart extras.
      if (!this.boxState) {
        this.boxState = { productQuantities: {}, addonQuantities: {}, selectedSize: '6 items' };
      }

      // Fetch /cart.js once and share with both consumers
      let cart = null;
      try {
        cart = await (await fetch('/cart.js')).json();
      } catch (_) {}

      await this.hydratePlanPriceFromCartIfMissing(cart);

      await Promise.all([
        this.loadProducts(),
        this.loadCartAddons(cart)
      ]);

      this.renderProducts();
      this.renderRecipientInfo();
    } catch (error) {
      if (this.productsGrid) {
        this.productsGrid.innerHTML = '<div class="text-center" style="padding: 4rem 2rem;"><p>Something went wrong. Please try again.</p></div>';
      }
    }
  }

  closeOtherDrawers() {
    // Close cart drawer
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.hasAttribute('active')) {
      cartDrawer.hide();
    }

    // Close any other drawers with active attribute (excluding gifting drawer - it's handled separately)
    const activeDrawers = document.querySelectorAll('.drawer[active]:not(custom-box-preview-modal):not(gifting-drawer)');
    activeDrawers.forEach(drawer => {
      if (drawer.hide) {
        drawer.hide();
      } else {
        drawer.removeAttribute('active');
        drawer.classList.remove('active');
      }
    });

    // Close search drawer
    const searchDrawer = document.querySelector('search-drawer');
    if (searchDrawer && searchDrawer.hasAttribute('active')) {
      searchDrawer.close();
    }

    // Close quick view through its own hide() so it also releases the body/html
    // scroll lock it holds; removing only the `active` attribute strands the
    // lock and leaves the page unscrollable.
    const quickView = document.querySelector('.quick-view[active]');
    if (quickView) {
      if (typeof quickView.hide === 'function') {
        quickView.hide();
      } else {
        quickView.removeAttribute('active');
      }
    }
  }

  close(immediate = false) {
    hideWildgrainUiBlockingOverlay();
    this.isOpen = false;
    this.classList.remove('active');
    this.removeAttribute('active');
    this.setAttribute('inert', '');

    let removed = false;
    const removeOverflow = () => {
      if (removed) return;
      removed = true;
      this.setAttribute('hidden', '');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.body.classList.remove('custom-box-preview-modal-open');
      document.documentElement.style.overflow = '';
    };

    if (immediate) {
      removeOverflow();
      return;
    }

    // Wait for transition to complete before hiding and removing overflow
    const overlay = this.querySelector('.fixed-overlay');
    if (overlay) {
      overlay.addEventListener('transitionend', removeOverflow, { once: true });
      overlay.addEventListener('transitioncancel', removeOverflow, { once: true });
    }
    // Safety fallback — transitionend never fires when the overlay had no
    // transition to run, which would leave the page permanently unscrollable.
    setTimeout(removeOverflow, 800);
  }

  async loadBoxState() {
    try {
      const boxStateStr = localStorage.getItem('wildgrain-custom-box-state');
      if (boxStateStr) {
        this.boxState = JSON.parse(boxStateStr);
      }
    } catch (error) {
      this.boxState = null;
    }
  }

  /**
   * When localStorage was saved before bundle variant prices loaded, selectedPlanPrice can be missing.
   * Fill it from the custom box line in the cart so the footer total is not $0.00.
   */
  async hydratePlanPriceFromCartIfMissing(cartData = null) {
    if (!this.boxState) return;
    const existing = parseFloat(this.boxState.selectedPlanPrice);
    if (Number.isFinite(existing) && existing > 0) return;

    try {
      const cart = cartData || await (await fetch('/cart.js')).json();
      const items = cart.items || [];
      const boxLine = items.find((item) => {
        const sku = (item.sku || '').toUpperCase();
        if (sku.startsWith('WGB_')) return true;
        if (item.selling_plan_allocation) return true;
        const title = (item.product_title || item.title || '').toLowerCase();
        return title.includes('custom box');
      });
      if (!boxLine) return;

      const qty = boxLine.quantity || 1;
      const lf = Number(boxLine.final_line_price);
      const lp = Number(boxLine.line_price);
      const lineCents = Number.isFinite(lf) ? lf : Number.isFinite(lp) ? lp : 0;
      if (lineCents <= 0) return;

      const perUnitDollars = lineCents / 100 / qty;
      if (!(perUnitDollars > 0)) return;

      this.boxState = { ...this.boxState, selectedPlanPrice: perUnitDollars };
    } catch (e) {
      // ignore
    }
  }

  async loadProducts() {
    try {
      // Load from cache first
      const cachedProducts = localStorage.getItem('wildgrain-products');
      const cacheTimestamp = localStorage.getItem('wildgrain-products-timestamp');
      const now = Date.now();
      const cacheAge = cacheTimestamp ? now - parseInt(cacheTimestamp) : Infinity;
      const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

      if (cachedProducts && cacheAge < CACHE_DURATION) {
        this.products = JSON.parse(cachedProducts);

        // Load free product separately if it exists
        await this.loadFreeProduct();

        // Load any products from box state that might not be in cache
        await this.loadMissingProducts();
        return;
      }

      // If not cached, fetch products
      const response = await fetch('/collections/addons-checkout-hide/products.json?limit=250&fields=id,title,body_html,product_type,tags,images,variants');
      const data = await response.json();
      this.products = this.processProducts(data.products);

      // Load free product separately if it exists
      await this.loadFreeProduct();

      // Load any products from box state that might not be in cache
      await this.loadMissingProducts();

      // Cache for future use
      localStorage.setItem('wildgrain-products', JSON.stringify(this.products));
      localStorage.setItem('wildgrain-products-timestamp', Date.now().toString());
    } catch (error) {
      // Error loading products
    }
  }

  async loadMissingProducts() {
    try {
      // Get all product IDs from box state
      const productQuantities = this.boxState?.productQuantities || {};
      const productIds = Object.keys(productQuantities);

      // Find any products that are not in the products array
      const missingProductIds = productIds.filter(id => {
        return !this.products.find(p => String(p.id) === String(id));
      });

      if (missingProductIds.length === 0) return;

      // Builder-catalog products (Recharge bundle data) may not be published
      // to the Online Store channel, so no storefront endpoint can resolve
      // them by id. The builder app persists a lightweight details map for
      // everything in the box (wildgrain-box-product-details) — use it first,
      // then fall back to the network for whatever is still unresolved.
      let savedDetails = {};
      try {
        savedDetails = JSON.parse(localStorage.getItem('wildgrain-box-product-details') || '{}');
      } catch (e) { /* corrupt entry — network fallback below */ }

      const stillMissingIds = [];
      missingProductIds.forEach((id) => {
        const d = savedDetails[id];
        if (d && d.title) {
          this.products.push({
            id,
            title: d.title,
            handle: d.handle || null,
            body_html: '',
            product_type: '',
            tags: [],
            image: d.image || null,
            variants: [{
              id: null,
              sku: '',
              price: d.price != null ? d.price : 0,
              compare_at_price: null
            }]
          });
        } else {
          stillMissingIds.push(id);
        }
      });

      if (stillMissingIds.length === 0) return;

      // Fetch all missing products in parallel for better performance.
      // /products/{...}.js takes a HANDLE — prefer the saved handle when the
      // details map has one (a bare numeric id only works if a redirect
      // exists, which is why unresolved ids used to drop rows silently).
      const fetchPromises = stillMissingIds.map(async (productId) => {
        try {
          const handleOrId = (savedDetails[productId] && savedDetails[productId].handle) || productId;
          const response = await fetch(`/products/${handleOrId}.js`);
          if (!response.ok) return null;

          const productData = await response.json();

          // Find first valid variant
          const validVariant = productData.variants.find(v =>
            v.sku?.startsWith('WG_') || v.sku?.startsWith('WGP_')
          );

          if (!validVariant) return null;

          return {
            id: productData.id,
            title: productData.title,
            body_html: productData.body_html || productData.description,
            product_type: productData.product_type,
            tags: productData.tags || [],
            image: productData.images && productData.images.length > 0 ? productData.images[0].src : (productData.image || null),
            variants: [{
              id: validVariant.id,
              sku: validVariant.sku,
              price: validVariant.price / 100, // Convert cents to dollars
              compare_at_price: validVariant.compare_at_price ? validVariant.compare_at_price / 100 : null
            }]
          };
        } catch (e) {
          // Failed to load product
          return null;
        }
      });

      const results = await Promise.all(fetchPromises);

      // Add successfully fetched products to the array
      results.forEach(product => {
        if (product) {
          this.products.push(product);
        }
      });
    } catch (error) {
      // Error loading missing products
    }
  }

  async loadFreeProduct() {
    const freeProductId = this.boxState?.freeProduct;

    if (!freeProductId) {
      return;
    }

    // Check if free product is already in products
    const existingProduct = this.products.find(p => String(p.id) === String(freeProductId));
    if (existingProduct) {
      return;
    }

    try {
      // Try to find the free product in cached products
      const cachedProducts = localStorage.getItem('wildgrain-products');
      if (!cachedProducts) {
        return;
      }

      const allCachedProducts = JSON.parse(cachedProducts);
      const freeProduct = allCachedProducts.find(p => String(p.id) === String(freeProductId));

      if (freeProduct) {
        this.products.push(freeProduct);
      } else {
        // Try to find the free product in session storage bundle data
        const bundleData = sessionStorage.getItem('wildgrain-bundle-data');

        if (bundleData && bundleData !== 'undefined' && bundleData !== 'null') {
          try {
            const bundle = JSON.parse(bundleData);

            // Validate that bundle is an object
            if (!bundle || typeof bundle !== 'object') {
              return;
            }

            let foundProduct = null;

            // Search through all collections and their products
            // Collections can be either an array or an object with collection IDs as keys
            if (bundle.collections) {
              const collectionsArray = Array.isArray(bundle.collections)
                ? bundle.collections
                : Object.values(bundle.collections);

              for (const collection of collectionsArray) {
                if (collection.products && Array.isArray(collection.products)) {
                  foundProduct = collection.products.find(p => String(p.id) === String(freeProductId));
                  if (foundProduct) {
                    break;
                  }
                }
              }
            }

            if (foundProduct) {
              // Find first valid variant
              const validVariant = foundProduct.variants.find(v =>
                v.sku?.startsWith('WG_') || v.sku?.startsWith('WGP_')
              );

              if (validVariant) {
                const processedProduct = {
                  id: foundProduct.id,
                  title: foundProduct.title,
                  body_html: foundProduct.body_html || foundProduct.description || '',
                  product_type: foundProduct.type || '',
                  tags: foundProduct.tags || [],
                  image: foundProduct.featured_image || (foundProduct.images && foundProduct.images.length > 0 ? foundProduct.images[0] : null),
                  variants: [{
                    id: validVariant.id,
                    sku: validVariant.sku,
                    price: validVariant.price / 100, // Convert cents to dollars
                    compare_at_price: validVariant.compare_at_price ? validVariant.compare_at_price / 100 : null
                  }]
                };

                this.products.push(processedProduct);
              }
            }
          } catch (sessionError) {
            // Error parsing session storage bundle data
          }
        }
      }
    } catch (error) {
      // Error loading free product from cache
    }
  }

  async loadCartAddons(cartData = null) {
    try {
      this.cartAddons = [];
      let cart = cartData;

      if (!cart) {
        try {
          const cartResponse = await fetch('/cart.js');
          if (cartResponse.ok) {
            cart = await cartResponse.json();
          }
        } catch (_) {
          cart = null;
        }
      }

      if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
        this.boxCartLine = null;
        return;
      }

      const boxItem = cart.items.find((item) => {
        const sku = (item.sku || '').toUpperCase();
        if (sku.startsWith('WGB_')) return true;
        if (item.selling_plan_allocation) return true;
        const title = (item.product_title || item.title || '').toLowerCase();
        return title.includes('custom box');
      });

      this.hasBoxInCart = !!boxItem;

      if (boxItem) {
        const allocation = boxItem.selling_plan_allocation || null;
        const plan = allocation && allocation.selling_plan ? allocation.selling_plan : null;
        const handle = boxItem.handle ||
          (typeof boxItem.url === 'string' ? (boxItem.url.match(/\/products\/([^\/?#]+)/)?.[1] || null) : null);
        this.boxCartLine = {
          key: boxItem.key,
          productId: boxItem.product_id,
          variantId: boxItem.variant_id || boxItem.id,
          handle,
          itemUrl: boxItem.url || '',
          quantity: boxItem.quantity || 1,
          productTitle: boxItem.product_title || boxItem.title || '',
          sellingPlanId: plan ? plan.id : null,
          sellingPlanName: plan ? plan.name : null
        };
      } else {
        this.boxCartLine = null;
      }

      let cachedList = [];
      try {
        const cachedProducts = localStorage.getItem('wildgrain-products');
        if (cachedProducts) cachedList = JSON.parse(cachedProducts) || [];
      } catch (_) {
        cachedList = [];
      }

      // First pass: categorize items and collect IDs that need fetching
      const itemsToProcess = [];
      const uncachedProductIds = new Set();

      for (const item of cart.items) {
        const title = item.title || '';
        const url = item.url || '';
        const isSubscription = typeof url === 'string' && url.includes('selling_plan=');
        const isCustomBox = title.toLowerCase().includes('custom box');
        if (isSubscription || isCustomBox) continue;

        const productId = String(item.product_id);
        const cachedProduct = cachedList.find(p => String(p.id) === productId) || null;

        itemsToProcess.push({ item, productId, cachedProduct });

        if (!cachedProduct) {
          uncachedProductIds.add(productId);
        }
      }

      // Fetch all uncached products in parallel instead of sequentially
      const fetchedProducts = new Map();
      if (uncachedProductIds.size > 0) {
        const fetches = [...uncachedProductIds].map(async (productId) => {
          try {
            const res = await fetch(`/products/${productId}.js`);
            if (res.ok) {
              fetchedProducts.set(productId, await res.json());
            }
          } catch (_) {}
        });
        await Promise.all(fetches);
      }

      // Second pass: build addons array using cached + parallel-fetched data
      const addons = [];
      for (const { item, productId, cachedProduct } of itemsToProcess) {
        const variantId = String(item.variant_id);
        const productData = cachedProduct || fetchedProducts.get(productId) || null;

        let variantSku = null;
        let variantPriceCents = null;
        if (productData && Array.isArray(productData.variants)) {
          const v = productData.variants.find(v => String(v.id) === variantId) || productData.variants[0];
          if (v) {
            variantSku = v.sku || null;
            if (cachedProduct && productData === cachedProduct) {
              variantPriceCents = typeof v.price === 'number' ? v.price * 100 : parseFloat(v.price) * 100;
            } else {
              variantPriceCents = typeof v.price === 'number' ? v.price : parseFloat(v.price) * 100;
            }
          }
        }

        if (variantSku && String(variantSku).startsWith('WGB_')) continue;

        let image = null;
        if (cachedProduct) {
          image = this.resolveImageSrc(cachedProduct.image);
        }
        if (!image && productData) {
          const pdImage = (productData.images && productData.images.length > 0 ? productData.images[0] : (productData.image || null));
          image = this.resolveImageSrc(pdImage);
        }

        const usedCartPrice = typeof item.price === 'number';
        const priceDollars = usedCartPrice ? (item.price / 100) : (variantPriceCents ? variantPriceCents / 100 : 0);

        addons.push({
          id: Number(productId),
          key: item.key || null,
          title: (item.title || '') || (cachedProduct ? cachedProduct.title : (productData ? productData.title : '')),
          image,
          quantity: item.quantity || 1,
          item_url: item.url || '',
          priceSource: usedCartPrice ? 'cart' : 'product',
          variants: [{ id: Number(variantId), sku: variantSku || '', price: priceDollars }]
        });
      }

      this.cartAddons = addons;
    } catch (error) {
      this.cartAddons = [];
    }
  }

  /**
   * Shorten a selling plan name for the discreet info pill:
   * "Delivery every 30 days" -> "Every 30 days".
   */
  formatFrequencyLabel(name) {
    if (!name) return '';
    return String(name)
      .replace(/^Delivery\s+every\s+/i, 'Every ')
      .replace(/^Deliver\s+every\s+/i, 'Every ')
      .replace(/^Every\s+/i, 'Every ')
      .trim();
  }

  /**
   * Turn a `deliveryFrequency` preference string like "every 30 days" into a
   * friendly label "Every 30 days". Returns '' if we can't pull a day count out.
   */
  formatFrequencyFromPreference(freqStr) {
    const n = String(freqStr || '').toLowerCase();
    const m = n.match(/(\d+)\s*day/);
    return m ? `Every ${m[1]} days` : '';
  }

  /**
   * Resolve the current delivery-frequency label. Prefers the cart line's
   * selling plan (source of truth once the box is in cart), falls back to
   * boxState.deliveryFrequency (preference while still building). Returns
   * '' if no frequency is known, if the box is not a subscription, or if the
   * box product title contains "trial".
   */
  getFrequencyDisplayLabel() {
    const boxTitle = (this.boxCartLine?.productTitle || '').toLowerCase();
    if (boxTitle.includes('trial')) return '';

    if (this.boxCartLine) {
      if (!this.boxCartLine.sellingPlanId || !this.boxCartLine.sellingPlanName) {
        return '';
      }
      return this.formatFrequencyLabel(this.boxCartLine.sellingPlanName);
    }
    if (this.boxState?.deliveryFrequency) {
      return this.formatFrequencyFromPreference(this.boxState.deliveryFrequency);
    }
    return '';
  }

  /**
   * HTML fragment for the inline, read-only delivery-frequency pill that sits
   * next to the "Box complete · N items" status line. Returns '' when no
   * frequency is available so nothing is rendered.
   */
  renderFrequencyInlineHTML() {
    const label = this.getFrequencyDisplayLabel();
    if (!label) return '';
    const safe = this.escapeHtml(label);
    return `
      <span
        class="custom-box-preview-modal__freq-info"
        role="note"
        aria-label="Delivery frequency: ${safe}"
      >
        <svg class="custom-box-preview-modal__freq-info-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
        </svg>
        <span class="custom-box-preview-modal__freq-info-label">${safe}</span>
      </span>
    `;
  }

  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  getRecipientProperties() {
    try {
      const cookieValue = this.getCookie('recipientProperties');
      if (!cookieValue) return null;
      return JSON.parse(cookieValue);
    } catch (error) {
      return null;
    }
  }

  async removeRecipientProperties() {
    // Clear the cookie
    document.cookie = 'recipientProperties=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    
    // Also clear recipient properties from cart items
    try {
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        throw new Error('Failed to fetch cart');
      }
      
      const cart = await cartResponse.json();
      
      // Find the custom box item (has WGB_ SKU or _rb_id property)
      const customBoxItem = cart.items.find(item => {
        const sku = item.sku || '';
        const hasWGB = sku.startsWith('WGB_');
        const hasRbId = item.properties && Object.keys(item.properties).some(key => key === '_rb_id' || item.properties[key] === '_rb_id');
        return hasWGB || hasRbId;
      });
      
      if (customBoxItem && customBoxItem.properties) {
        // Properties can be either an array of objects [{name, value}] or a plain object
        const isArray = Array.isArray(customBoxItem.properties);
        
        // Check if item has recipient properties
        let hasRecipientProps = false;
        if (isArray) {
          hasRecipientProps = customBoxItem.properties.some(prop => 
            prop.name === 'recipient_first_name_otp' || 
            prop.name === 'recipient_last_name_otp' || 
            prop.name === 'recipient_email_otp' || 
            prop.name === 'gift_message'
          );
        } else {
          hasRecipientProps = Object.keys(customBoxItem.properties).some(key => 
            key === 'recipient_first_name_otp' || 
            key === 'recipient_last_name_otp' || 
            key === 'recipient_email_otp' || 
            key === 'gift_message'
          );
        }
        
        if (hasRecipientProps) {
          // Create updated properties without recipient properties
          let updatedProperties;
          if (isArray) {
            // Filter out recipient properties from array
            updatedProperties = customBoxItem.properties.filter(prop => 
              prop.name !== 'recipient_first_name_otp' && 
              prop.name !== 'recipient_last_name_otp' && 
              prop.name !== 'recipient_email_otp' && 
              prop.name !== 'gift_message'
            );
          } else {
            // Remove recipient properties from object
            updatedProperties = { ...customBoxItem.properties };
            delete updatedProperties['recipient_first_name_otp'];
            delete updatedProperties['recipient_last_name_otp'];
            delete updatedProperties['recipient_email_otp'];
            delete updatedProperties['gift_message'];
          }
          
          // Update cart item using JSON format (same as checkout-addons.js)
          const updateResponse = await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: customBoxItem.key || customBoxItem.id,
              properties: updatedProperties
            })
          });
          
          if (updateResponse.ok) {
            const updatedCart = await updateResponse.json();
            // Notify listeners that cart was updated
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: updatedCart } }));
            
            // Reload cart addons since cart changed
            await this.loadCartAddons();
            this.renderProducts();
          }
        }
      }
    } catch (error) {
      // Error clearing cart properties - cookie is still cleared
      console.error('Error clearing recipient properties from cart:', error);
    }
    
    // Re-render to remove the info pane
    this.renderRecipientInfo();
  }

  isTrialPage() {
    const pathname = window.location.pathname.toLowerCase();
    return pathname.includes('trial');
  }

  renderRecipientInfo() {
    const recipientInfoContainer = this.querySelector('[data-recipient-info]');
    if (!recipientInfoContainer) return;

    // Check if URL includes "trial" (case insensitive) and cookie exists
    if (!this.isTrialPage()) {
      recipientInfoContainer.innerHTML = '';
      return;
    }

    const recipientProperties = this.getRecipientProperties();
    if (!recipientProperties) {
      recipientInfoContainer.innerHTML = '';
      return;
    }

    const firstName = recipientProperties.first_name || '';
    const lastName = recipientProperties.last_name || '';
    const email = recipientProperties.email || '';
    const giftMessage = recipientProperties.gift_message || '';

    recipientInfoContainer.innerHTML = `
      <div class="flex items-start gap-3 w-full" style="padding: 1rem 1.6rem; background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%); border: 1px solid #93c5fd; border-radius: var(--blocks-radius-md);">
        <div class="flex-grow flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" style="color: #3b82f6; flex-shrink: 0;">
              <path d="M9 1.5C5.2725 1.5 2.25 4.5225 2.25 8.25C2.25 12.7275 8.25 16.5 9 16.5C9.75 16.5 15.75 12.7275 15.75 8.25C15.75 4.5225 12.7275 1.5 9 1.5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M9 10.5C10.6569 10.5 12 9.15685 12 7.5C12 5.84315 10.6569 4.5 9 4.5C7.34315 4.5 6 5.84315 6 7.5C6 9.15685 7.34315 10.5 9 10.5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span class="font-body-bold text-sm" style="color: #1e40af;">Gift Recipient</span>
          </div>
          <div class="flex flex-col gap-1 text-sm" style="color: #1e3a8a;">
            ${firstName || lastName ? `<div class="font-body-medium">${firstName} ${lastName}</div>` : ''}
            ${email ? `<div class="text-xs text-subtext">${email}</div>` : ''}
            ${giftMessage ? `<div class="text-xs mt-1 italic" style="color: #475569;">"${giftMessage}"</div>` : ''}
          </div>
        </div>
        <button 
          type="button"
          class="flex items-center justify-center flex-shrink-0 recipient-remove-btn"
          data-remove-recipient
          aria-label="Remove recipient information"
          style="width: 28px; height: 28px; border-radius: 50%; background: rgba(59, 130, 246, 0.1); color: #3b82f6; transition: all 0.2s;"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    `;

    // Attach event listeners for remove button
    const removeButton = recipientInfoContainer.querySelector('[data-remove-recipient]');
    if (removeButton) {
      removeButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.removeRecipientProperties();
      });
      
      // Add hover effects
      removeButton.addEventListener('mouseenter', () => {
        removeButton.style.background = 'rgba(59, 130, 246, 0.2)';
      });
      removeButton.addEventListener('mouseleave', () => {
        removeButton.style.background = 'rgba(59, 130, 246, 0.1)';
      });
    }
  }

  resolveImageSrc(image) {
    if (!image) return null;
    if (typeof image === 'string') return image;
    if (typeof image === 'object') {
      return image.src || image.url || image.originalSrc || image.original_url || null;
    }
    return null;
  }

  getResizedImageUrl(imageUrl, size = '200x200') {
    if (!imageUrl) return null;

    // Shopify image URL format: add size parameter before file extension
    // Example: image.jpg -> image_200x200.jpg
    const extensionIndex = imageUrl.lastIndexOf('.');
    if (extensionIndex === -1) return imageUrl;

    // Check if already has size parameter
    if (imageUrl.includes('_' + size)) return imageUrl;

    return imageUrl.substring(0, extensionIndex) + '_' + size + imageUrl.substring(extensionIndex);
  }

  async syncCartAddonsFromCart(cartJson = null) {
    try {
      const cart = cartJson || await (await fetch('/cart.js')).json();
      const addons = [];
      (cart.items || []).forEach((item) => {
        // Exclude the subscription/custom box
        const isSubscription = !!item.selling_plan_allocation;
        const isCustomBox = (item.product_title || item.title || '').toLowerCase().includes('custom box');
        if (isSubscription || isCustomBox) return;

        // Filter out WGB_ SKU
        const sku = item.sku || '';
        if (sku && sku.startsWith('WGB_')) return;

        const productId = Number(item.product_id);
        const variantId = Number(item.variant_id || item.id);
        const image = this.resolveImageSrc(item.image || null);
        const priceDollars = (typeof item.price === 'number' ? item.price : parseFloat(item.price || 0)) / 100;

        addons.push({
          id: productId,
          key: item.key || null,
          title: item.product_title || item.title || '',
          image,
          quantity: item.quantity || 1,
          item_url: item.url || '',
          priceSource: 'cart',
          variants: [{ id: variantId, sku: sku || '', price: priceDollars }]
        });
      });
      this.cartAddons = addons;
    } catch (e) {
      // Failed to sync cart add-ons
    }
  }

  async changeCartQuantity(variantId, newQuantity) {
    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', String(newQuantity));

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        body: formData
      });
      const cart = await response.json();

      // Notify listeners and sync our local view
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      await this.syncCartAddonsFromCart(cart);
      this.renderProducts();
    } catch (error) {
      // Failed to update cart quantity
    }
  }

  processProducts(products) {
    return products.reduce((acc, product) => {
      const validVariant = product.variants.find(v =>
        v.sku?.startsWith('WG_') || v.sku?.startsWith('WGP_')
      );

      if (!validVariant) {
        return acc;
      }

      acc.push({
        id: product.id,
        title: product.title,
        handle: product.handle,
        body_html: product.body_html,
        product_type: product.product_type,
        tags: product.tags,
        image: product.images && product.images.length > 0 ? product.images[0].src : null,
        variants: [{
          id: validVariant.id,
          sku: validVariant.sku,
          price: validVariant.price,
          compare_at_price: validVariant.compare_at_price
        }]
      });

      return acc;
    }, []);
  }

  async renderProducts() {
    if (!this.productsGrid || !this.boxState) return;
    // Don't repaint over the locked "Clearing your box…" state mid-clear.
    if (this.isClearingBox) return;

    const productQuantities = this.boxState.productQuantities || {};
    const selectedSize = this.boxState.selectedSize || '7 items';
    const freeProductId = this.boxState?.freeProduct;

    // Calculate totals for footer button and product categorization (exclude free product)
    let totalItems = 0;
    Object.entries(productQuantities).forEach(([productId, qty]) => {
      totalItems += qty;
    });
    const boxSize = this.getBoxSize(selectedSize);

    // Update footer button based on box state
    this.updateFooterButton(totalItems >= boxSize);

    // Update header info using shared method
    const headerInfo = this.querySelector('[data-modal-header-info]');
    if (headerInfo) {
      headerInfo.innerHTML = this.generateHeaderHTML();
    }

    // Categorize products - Show ONE card per product with the actual quantity
    const planItems = [];
    const addonItems = [];
    const freeItems = [];

    // Check for free product from box state
    if (freeProductId) {
      const freeProduct = this.products.find(p => String(p.id) === String(freeProductId))
        || WG_FREE_ITEM_FALLBACKS[String(freeProductId)];
      if (freeProduct) {
        freeItems.push({ ...freeProduct, actualQuantity: 1, isFree: true });
      }
    }

    // Hot-dog-bun promo: 6/8/12-item boxes only, matches box builder's eligibility check.
    // Buns contain gluten/dairy/eggs, so only offer on the omnivore Variety Box plan.
    const hotdogFlag =
      (() => { try { return sessionStorage.getItem('wg_hotdog_promo') === 'true' } catch (e) { return false } })() ||
      new URLSearchParams(window.location.search).get('hotdog_promo') === 'true';
    const selectedPlan = this.boxState?.selectedPlan;
    const isVarietyBoxPlan = !selectedPlan || selectedPlan === 'Variety Box';
    if (hotdogFlag && boxSize >= 6 && isVarietyBoxPlan) {
      freeItems.push({ ...WG_HOTDOG_PROMO_FREE_ITEM, actualQuantity: 1, isFree: true });
    }

    // Group products by ID and show one card per product
    // Items are categorized as plan or addon based on box size
    let itemsProcessed = 0;
    Object.entries(productQuantities).forEach(([productId, quantity]) => {
      const product = this.products.find(p => String(p.id) === String(productId));
      if (!product || quantity <= 0) return;

      // Free product is shown in plan items like any other - the free banner is separate

      // Determine if items are in plan or addon section
      if (itemsProcessed < boxSize) {
        // At least some items are in plan
        const planQuantity = Math.min(quantity, boxSize - itemsProcessed);
        const addonQuantity = quantity - planQuantity;

        if (planQuantity > 0) {
          planItems.push({ ...product, actualQuantity: planQuantity, isPlan: true });
        }
        if (addonQuantity > 0) {
          addonItems.push({ ...product, actualQuantity: addonQuantity, isAddon: true });
        }
      } else {
        // All items are addons
        addonItems.push({ ...product, actualQuantity: quantity, isAddon: true });
      }

      itemsProcessed += quantity;
    });

    // Render sections
    let html = '';

    if (this.isPortalMode) {
      // Portal: the app tray's single-card layout (free row, item rows,
      // dashed empty slots, Clear box) instead of the labeled theme sections.
      html = this.renderPortalTrayHTML({ freeItems, planItems, addonItems, totalItems, boxSize });
    } else {

    // Free Items Section (if any)
    if (freeItems.length > 0) {
      html += `
        <div class="custom-box-preview-modal__section flex flex-col">
          <h3 class="custom-box-preview-modal__section-title">FREE ITEMS</h3>
          <div class="flex flex-col">
            ${freeItems.map(item => this.renderProductCard(item, true)).join('')}
          </div>
        </div>
      `;
    }

    // Plan Items Section
    if (planItems.length > 0) {
      const planItemsExplore =
        totalItems >= boxSize && !this.isOnBundleCustomizeProductPage()
          ? this.getPlanItemsExploreLinkHTML()
          : '';
      html += `
        <div class="custom-box-preview-modal__section flex flex-col">
          <h3 class="custom-box-preview-modal__section-title">PLAN ITEMS</h3>
          ${planItemsExplore}
          <div class="flex flex-col">
            ${planItems.map(item => this.renderProductCard(item, false)).join('')}
          </div>
        </div>
      `;
    }

    // Add-ons Section from cart (requested)
    if (this.cartAddons && this.cartAddons.length > 0) {
      html += `
        <div class="custom-box-preview-modal__section custom-box-preview-modal__section--border flex flex-col">
          <h3 class="custom-box-preview-modal__section-title">ADD-ONS</h3>
          <div class="flex flex-col">
            ${this.cartAddons.map(item => this.renderCartAddonCard(item)).join('')}
          </div>
        </div>
      `;
    } else if (addonItems.length > 0) {
      // Fallback: show add-ons derived from box state if no cart add-ons exist
      html += `
        <div class="custom-box-preview-modal__section custom-box-preview-modal__section--border flex flex-col">
          <h3 class="custom-box-preview-modal__section-title">ADD-ONS</h3>
          <div class="flex flex-col">
            ${addonItems.map(item => this.renderProductCard(item, false, true)).join('')}
          </div>
        </div>
      `;
    }

    // Empty state
    if (planItems.length === 0 && addonItems.length === 0 && freeItems.length === 0) {
      html = '<div class="text-center grid gap-10" style="padding: 4rem 2rem;"><div class="grid gap-3"><p class="h4">No items in your box yet.</p><p>Start adding products!</p></div></div>';
    }

    } // end classic (non-portal) sections

    // Preserve the beam widget across the innerHTML replacement.
    // <beam-select-nonprofit> is a Stencil web component: once connected it
    // hydrates its config (apiKey/storeId) into instance state and holds the
    // shopper's nonprofit selection as JS properties. cloneNode() copies only
    // attributes/markup, so a clone re-inits without a valid apiKey (throwing
    // "Beam Impact – Missing Required Config Values: apiKey") and renders with
    // no selection. Moving the *same* live node (detach + re-append is a plain
    // disconnect/reconnect) keeps its hydrated state and selection intact.
    const beamWidget = this.productsGrid.querySelector('[data-beam-widget="select-nonprofit"]');
    if (beamWidget) {
      beamWidget.remove();
    }

    this.productsGrid.innerHTML = html;

    // Re-append the same live widget node if it existed
    if (beamWidget) {
      this.productsGrid.append(beamWidget);
    } else if (typeof window.wgLoadBeamSelectNonprofit === 'function') {
      // No widget was preserved — e.g. the injector's observer missed an early
      // modal-open (box opened before the page finished loading). Ensure it's
      // added now that the box data has loaded.
      window.wgLoadBeamSelectNonprofit();
    }

    // Portal: collapse the widget behind the app-style one-line pitch. The
    // snippet's own observer can also inject the widget asynchronously, so
    // watch the grid and (idempotently) re-apply whenever it appears.
    if (this.isPortalMode) {
      this.applyPortalBeamTreatment();
      if (!this.beamGridObserver && typeof MutationObserver !== 'undefined') {
        this.beamGridObserver = new MutationObserver(() => this.applyPortalBeamTreatment());
        this.beamGridObserver.observe(this.productsGrid, { childList: true });
      }
    }

    // Attach event listeners
    this.attachProductListeners();
    this.attachCartAddonListeners();
  }

  /** "Maple Belgian Waffle (6-pack)" → { name: "Maple Belgian Waffle", pack: "6-pack" } */
  splitPackTitle(title) {
    const m = /^(.*?)\s*\(([^)]*pack[^)]*)\)\s*$/i.exec(title || '');
    if (m) return { name: m[1], pack: m[2] };
    return { name: title || '', pack: '' };
  }

  /**
   * Portal tray body — the app's Your Box card, adapted for the theme:
   * one rounded card holding the gold free-item row, the picked items
   * (circular thumb · split title/pack · pill stepper), one dashed
   * "Add an item" row per open slot (routes to the PDP to keep picking),
   * and a Clear box action. Extras (beyond capacity or already in cart)
   * follow in their own card. Steppers reuse the existing delegated
   * [data-action]/[data-cart-action] handlers.
   */
  renderPortalTrayHTML({ freeItems, planItems, addonItems, totalItems, boxSize }) {
    const isBoxFull = totalItems >= boxSize;
    const emptySlots = Math.max(0, boxSize - totalItems);
    const hasAnyItems = planItems.length > 0 || addonItems.length > 0;

    const freeRows = freeItems.map((item) => {
      const img = this.getResizedImageUrl(item.image, '120x120');
      const name = this.escapeHtml(this.splitPackTitle(item.title).name);
      return `
        <div class="wg-tray-row wg-tray-row--free" data-product-id="${item.id}">
          <span class="wg-tray-thumb wg-tray-thumb--free">
            ${img ? `<img src="${img}" alt="" loading="lazy" width="120" height="120">` : ''}
          </span>
          <span class="wg-tray-copy">
            <span class="wg-tray-title">${name}</span>
            <span class="wg-tray-sub">${this.escapeHtml(item.freeSubtitle || 'Free in every box')}</span>
          </span>
          <span class="wg-tray-free-badge">FREE</span>
        </div>
      `;
    }).join('');

    const itemRows = planItems.map((item) => this.renderPortalTrayRow(item, { isBoxFull })).join('');

    const slotRows = Array.from({ length: emptySlots }).map(() => `
      <button type="button" class="wg-tray-slot" data-wg-add-item>
        <span class="wg-tray-slot-circle" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path stroke-linecap="round" d="M12 5v14M5 12h14"/>
          </svg>
        </span>
        Add an item
      </button>
    `).join('');

    // Extras: state-derived add-ons (beyond capacity) and cart add-ons.
    const cartAddonRows = (this.cartAddons && this.cartAddons.length > 0)
      ? this.cartAddons.map((item) => this.renderPortalCartAddonRow(item)).join('')
      : '';
    const stateAddonRows = (!cartAddonRows && addonItems.length > 0)
      ? addonItems.map((item) => this.renderPortalTrayRow(item, { isBoxFull, isAddon: true })).join('')
      : '';
    const extrasRows = cartAddonRows || stateAddonRows;
    const extrasCard = extrasRows ? `
      <p class="wg-tray-extras-label">Extras — ship in the same box</p>
      <div class="wg-tray-card">${extrasRows}</div>
    ` : '';

    // Clear sits under ALL line items (box card + extras card) and clears
    // everything: box picks, pending extras, and cart extra lines.
    const clearRow = (hasAnyItems || extrasRows) ? `
      <button type="button" class="wg-tray-clear" data-wg-clear-box>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/>
        </svg>
        Clear box
      </button>
    ` : '';

    return `
      <div class="wg-tray-card">
        ${freeRows}
        ${itemRows}
        ${slotRows}
      </div>
      ${extrasCard}
      ${clearRow}
    `;
  }

  /** One tray item row: circular thumb · title/pack (or add-on price) · pill stepper. */
  renderPortalTrayRow(product, { isBoxFull = false, isAddon = false } = {}) {
    const qty = product.actualQuantity || 0;
    const img = this.getResizedImageUrl(product.image, '120x120');
    const { name, pack } = this.splitPackTitle(product.title);
    const price = product.variants && product.variants[0] ? parseFloat(product.variants[0].price) : 0;
    const sub = isAddon
      ? `Add-on · $${price.toFixed(2)}`
      : pack;
    const nameSafe = this.escapeHtml(name);
    const removeIcon = `
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <path stroke-linecap="round" d="M6 6l12 12M18 6L6 18"/>
      </svg>`;
    const minusIcon = `
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <path stroke-linecap="round" d="M5 12h14"/>
      </svg>`;
    return `
      <div class="wg-tray-row" data-product-id="${product.id}">
        <span class="wg-tray-thumb">
          ${img ? `<img src="${img}" alt="" loading="lazy" width="120" height="120">` : ''}
        </span>
        <span class="wg-tray-copy">
          <span class="wg-tray-title">${nameSafe}</span>
          ${sub ? `<span class="wg-tray-sub">${this.escapeHtml(sub)}</span>` : ''}
        </span>
        <span class="wg-tray-stepper">
          <button type="button" data-action="decrease" data-product-id="${product.id}"
            aria-label="${qty === 1 ? `Remove ${nameSafe} from box` : `Decrease ${nameSafe} quantity`}">
            ${qty === 1 ? removeIcon : minusIcon}
          </button>
          <span class="wg-tray-qty" aria-hidden="true">${qty}</span>
          <button type="button" data-action="increase" data-product-id="${product.id}"
            aria-label="Increase ${nameSafe} quantity" ${isBoxFull ? 'disabled' : ''}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path stroke-linecap="round" d="M12 5v14M5 12h14"/>
            </svg>
          </button>
        </span>
      </div>
    `;
  }

  /** Cart add-on row — same look, wired to the cart quantity handlers. */
  renderPortalCartAddonRow(product) {
    const qty = product.quantity || 1;
    // loadCartAddons/syncCartAddonsFromCart always store dollars, whatever the
    // priceSource — no cents heuristic here (it turned a $134 add-on into $1.34).
    const price = product.variants && product.variants[0] ? parseFloat(product.variants[0].price) : 0;
    const variantId = product.variants && product.variants[0] ? product.variants[0].id : null;
    const img = this.getResizedImageUrl(product.image, '120x120');
    const { name } = this.splitPackTitle(product.title);
    const nameSafe = this.escapeHtml(name);
    return `
      <div class="wg-tray-row" data-product-id="${product.id}" ${variantId ? `data-variant-id="${variantId}"` : ''}>
        <span class="wg-tray-thumb">
          ${img ? `<img src="${img}" alt="" loading="lazy" width="120" height="120">` : ''}
        </span>
        <span class="wg-tray-copy">
          <span class="wg-tray-title">${nameSafe}</span>
          <span class="wg-tray-sub">Add-on · $${price.toFixed(2)}</span>
        </span>
        <span class="wg-tray-stepper">
          <button type="button" data-cart-action="decrease" data-variant-id="${variantId}"
            aria-label="${qty === 1 ? `Remove ${nameSafe} from cart` : `Decrease ${nameSafe} quantity`}">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              ${qty === 1 ? '<path stroke-linecap="round" d="M6 6l12 12M18 6L6 18"/>' : '<path stroke-linecap="round" d="M5 12h14"/>'}
            </svg>
          </button>
          <span class="wg-tray-qty" aria-hidden="true">${qty}</span>
          <button type="button" data-cart-action="increase" data-variant-id="${variantId}" aria-label="Increase ${nameSafe} quantity">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path stroke-linecap="round" d="M12 5v14M5 12h14"/>
            </svg>
          </button>
        </span>
      </div>
    `;
  }

  /** Dashed "Add an item" slot → same destination as the keep-building CTA. */
  handlePortalAddItem() {
    if (this.isOnBundleCustomizeProductPage()) {
      this.close();
      return;
    }
    window.location.href = this.buildBundleCustomizeUrl();
  }

  /**
   * Clear box with a two-tap confirm (destructive; mirrors the app's inline
   * confirm). Clears EVERYTHING the tray lists: box picks and pending
   * extras in the builder state, plus one-time extra lines already in the
   * real Shopify cart. Cart lines are zeroed in ONE atomic /cart/update.js
   * call (sequential /cart/change.js was slow enough that users could
   * dismiss the modal mid-clear and leave items behind); while it runs the
   * tray is locked behind a spinner and the wait overlay.
   */
  async handlePortalClearBox(btn) {
    if (btn.dataset.confirm !== '1') {
      btn.dataset.confirm = '1';
      btn.classList.add('wg-tray-clear--confirm');
      btn.innerHTML = 'Tap again to clear everything';
      // Auto-reset if they don't confirm within a few seconds.
      setTimeout(() => {
        if (btn.isConnected && btn.dataset.confirm === '1') {
          delete btn.dataset.confirm;
          this.renderProducts();
        }
      }, 4000);
      return;
    }
    if (this.isClearingBox) return;
    this.isClearingBox = true;
    // Confirmed — neutralize the auto-reset timeout and lock the tray with a
    // spinner so the user knows to wait (renderProducts also early-returns
    // while isClearingBox, so nothing can wipe this state mid-flight).
    delete btn.dataset.confirm;
    btn.disabled = true;
    btn.classList.remove('wg-tray-clear--confirm');
    btn.classList.add('wg-tray-clear--busy');
    btn.innerHTML = '<span class="wg-tray-clear-spinner" aria-hidden="true"></span>Clearing your box…';
    if (this.productsGrid) this.productsGrid.classList.add('wg-tray-clearing');
    const hasCartLines = (this.cartAddons || []).length > 0;
    if (hasCartLines) showWildgrainUiBlockingOverlay();
    try {
      const boxStateStr = localStorage.getItem('wildgrain-custom-box-state');
      if (boxStateStr) {
        const boxState = JSON.parse(boxStateStr);
        boxState.productQuantities = {};
        boxState.addonQuantities = {};
        boxState.boxFull = false;
        localStorage.setItem('wildgrain-custom-box-state', JSON.stringify(boxState));
        document.dispatchEvent(new CustomEvent('custom-box-state:updated', { detail: boxState }));
        window.dispatchEvent(new CustomEvent('customBoxStateChanged', { detail: boxState }));
        this.boxState = boxState;
      }

      const cartLines = (this.cartAddons || []).slice();
      if (cartLines.length > 0) {
        // Line item keys are exact (safe if two lines share a variant);
        // variant id is the fallback for addons synced before keys existed.
        const updates = {};
        for (const addon of cartLines) {
          const lineKey = addon.key || (addon.variants && addon.variants[0] && addon.variants[0].id);
          if (lineKey) updates[String(lineKey)] = 0;
        }
        if (Object.keys(updates).length > 0) {
          const res = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates })
          });
          if (!res.ok) throw new Error(`cart update failed (${res.status})`);
          const cart = await res.json();
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        }
        this.cartAddons = [];
      }

      this.close();
    } catch (e) {
      // Failed mid-clear: re-sync from the real cart and re-render so the
      // tray shows what's actually left, instead of closing over stale state.
      this.isClearingBox = false;
      try { await this.loadCartAddons(); } catch (_) { /* keep local view */ }
      this.renderProducts();
    } finally {
      this.isClearingBox = false;
      if (this.productsGrid) this.productsGrid.classList.remove('wg-tray-clearing');
      hideWildgrainUiBlockingOverlay();
    }
  }

  /**
   * Portal Beam treatment — the bundle app's BeamDonation UX in vanilla JS:
   * collapsed, the section is a single flowing line ("🍽️ Select a food bank
   * and 2 meals will be donated… Select food bank") — or, once chosen, the
   * food bank's name with a "Change food bank" action. Expanded, the real
   * Beam widget shows with a chevron to collapse. Idempotent: renderProducts
   * wipes the grid's HTML but re-appends the same live widget node, so the
   * header/chevron are recreated as needed and state lives on `this`.
   */
  applyPortalBeamTreatment() {
    if (!this.isPortalMode || !this.productsGrid) return;
    const widget = this.productsGrid.querySelector('[data-beam-widget="select-nonprofit"]');
    if (!widget) return;

    let header = this.productsGrid.querySelector('[data-wg-beam-header]');
    if (!header) {
      header = document.createElement('button');
      header.type = 'button';
      header.className = 'wg-beam-collapsed';
      header.setAttribute('data-wg-beam-header', '');
      header.setAttribute('aria-expanded', 'false');
      header.addEventListener('click', () => {
        this.beamOpen = true;
        this.syncPortalBeam();
      });
      widget.parentNode.insertBefore(header, widget);
    }

    let collapseBtn = widget.querySelector('[data-wg-beam-collapse]');
    if (!collapseBtn) {
      collapseBtn = document.createElement('button');
      collapseBtn.type = 'button';
      collapseBtn.className = 'wg-beam-collapse-btn';
      collapseBtn.setAttribute('data-wg-beam-collapse', '');
      collapseBtn.setAttribute('aria-label', 'Hide food banks');
      collapseBtn.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" style="transform: rotate(180deg)"><path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/></svg>';
      collapseBtn.addEventListener('click', () => {
        this.beamOpen = false;
        this.syncPortalBeam();
      });
      widget.insertBefore(collapseBtn, widget.firstChild);
    }

    this.rewriteBeamSubheader(widget);
    this.syncPortalBeam();
  }

  /** Sync collapsed-header copy and open/closed visibility with state. */
  syncPortalBeam() {
    if (!this.isPortalMode || !this.productsGrid) return;
    const widget = this.productsGrid.querySelector('[data-beam-widget="select-nonprofit"]');
    const header = this.productsGrid.querySelector('[data-wg-beam-header]');
    if (!widget || !header) return;
    const name = this.readBeamNonprofitName();
    const safeName = name ? this.escapeHtml(name) : null;
    header.innerHTML = safeName
      ? `🍽️ 2 meals will be donated to ${safeName} with every order, at no extra cost. <span class="wg-beam-action">Change food bank</span>`
      : '🍽️ Select a food bank and 2 meals will be donated there for you with every order - at no extra cost! <span class="wg-beam-action">Select food bank</span>';
    header.hidden = this.beamOpen;
    header.setAttribute('aria-expanded', this.beamOpen ? 'true' : 'false');
    widget.classList.toggle('wg-beam-open', this.beamOpen);
  }

  /**
   * Name of the already-chosen food bank, or null. The Beam SDK keeps its
   * state in scoped localStorage (`beam_<key>_key_<apiKey>`): "nonprofit"
   * holds the selected id, "chainNonprofits" caches the list we map it to.
   */
  readBeamNonprofitName() {
    try {
      const el = this.productsGrid?.querySelector('beam-select-nonprofit');
      const apiKey = el?.getAttribute('apiKey') || el?.getAttribute('apikey');
      if (!apiKey) return null;
      const scoped = (key) => localStorage.getItem(`beam_${key}_key_${apiKey}`);
      const id = scoped('nonprofit');
      if (!id || id === 'null' || id === 'undefined') return null;
      const cached = JSON.parse(scoped('chainNonprofits') || 'null');
      const match = (cached?.data?.nonprofits || []).find(
        (n) => String(n?.nonprofit?.id) === String(id)
      );
      return match?.nonprofit?.name || null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Beam's open-view sub-header ("…with every box") is store-level copy from
   * Beam's dashboard — no attribute overrides it. The donation is per Shopify
   * ORDER (per-order wording requested by Beam, Aug 2026), so rewrite the one
   * string in place; Lit re-renders it, so keep re-applying via an observer.
   */
  rewriteBeamSubheader(widget) {
    if (this.beamCopyObserver) return; // once per live widget node
    const COPY = 'Select a food bank and 2 meals will be donated there for you with every order - at no extra cost!';
    const apply = () => {
      const root = widget.querySelector('beam-select-nonprofit')?.shadowRoot;
      if (!root) return false;
      root.querySelectorAll('[part~="description"]').forEach((el) => {
        if (el.textContent !== COPY && /2 meals will be donated/i.test(el.textContent)) {
          el.textContent = COPY;
        }
      });
      return true;
    };
    let tries = 0;
    const start = () => {
      const root = widget.querySelector('beam-select-nonprofit')?.shadowRoot;
      if (!root) {
        // Widget upgrades asynchronously (module script) — retry for ~30s.
        if (tries++ < 120) setTimeout(start, 250);
        return;
      }
      apply();
      if (typeof MutationObserver !== 'undefined') {
        this.beamCopyObserver = new MutationObserver(apply);
        this.beamCopyObserver.observe(root, { childList: true, subtree: true, characterData: true });
      }
    };
    start();
  }

  renderProductCard(product, isFree = false, isAddon = false) {
    const productQuantities = this.boxState?.productQuantities || {};
    const currentQuantity = productQuantities[product.id] || product.actualQuantity || 0;
    const price = product.variants && product.variants[0] ? parseFloat(product.variants[0].price) : 0;

    const freeProductId = this.boxState?.freeProduct;
    let totalItems = 0;
    Object.entries(productQuantities).forEach(([pid, qty]) => {
      totalItems += qty;
    });
    const boxSize = this.getBoxSize(this.boxState?.selectedSize);
    const isBoxFull = this.boxState?.boxFull === true || totalItems >= boxSize;

    // Thumbnail sized like compact Member Deals rows (52px display → 120px src)
    const resizedImage = this.getResizedImageUrl(product.image, '120x120');
    const minusAria =
      currentQuantity === 1
        ? `Remove ${product.title} from box`
        : 'Decrease quantity';
    const minusAriaSafe = minusAria.replace(/&/g, '&amp;').replace(/"/g, '&quot;');

    return `
      <div class="cart-item custom-box-preview-modal__line ${isFree ? 'custom-box-preview-modal__line--free' : ''}" data-product-id="${product.id}">
        <div class="cart-item__product flex items-center gap-2">
          <div class="cart-item__media blocks-radius-sm media-wrapper custom-box-preview-modal__thumb">
            ${resizedImage
              ? `<img src="${resizedImage}" alt="${product.title}" loading="lazy" width="120" height="120" style="width: 100%; height: 100%; object-fit: cover; display: block;">`
              : WG_BUNDLE_IMAGE_PLACEHOLDER}
          </div>
          <div class="cart-item__details flex-grow flex flex-col gap-1 min-w-0">
            ${isFree ? `
              <div class="flex items-start justify-between gap-2 w-full">
                <span class="cart-item__title text-pcard-title cart-item__title--free">${product.title}</span>
                <span class="cart-item__free-badge" aria-hidden="true">Free gift</span>
              </div>
              <div class="cart-item__options">
                <span class="cart-item__option-value text-sm cart-item__free-sub">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5" fill="none"/>
                    <path d="M5 8l2 2 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  ${product.freeSubtitle || `Free in every box · Save $${price.toFixed(2)}`}
                </span>
              </div>
            ` : `
            <div class="grid min-w-0">
              <div class="block min-w-0">
                <span class="cart-item__title text-pcard-title">${product.title}</span>
              </div>
              ${isAddon ? `
                <div class="cart-item__options">
                  <span class="cart-item__option-value text-sm text-subtext">Add-on • $${price.toFixed(2)}</span>
                </div>
              ` : ''}
            </div>
            `}
          </div>
          ${!isFree ? `
          <div class="cart-item__action flex flex-row items-center justify-end gap-2 flex-shrink-0">
            <div class="cart-quantity quantity">
              <button type="button" name="minus" class="quantity__button ${currentQuantity === 1 ? 'qty-stepper-remove' : ''}" data-action="decrease" data-product-id="${product.id}" aria-label="${minusAriaSafe}">
                ${currentQuantity === 1 ? WG_BUNDLE_TRASH_SVG : WG_BUNDLE_MINUS_SVG}
              </button>
              <input class="quantity__input" type="number" value="${currentQuantity}" readonly style="pointer-events: none;">
              <button type="button" name="plus" class="quantity__button ${isBoxFull ? 'disabled' : ''}" data-action="increase" data-product-id="${product.id}" aria-label="Increase quantity" ${isBoxFull ? 'disabled style="opacity: 0.5; cursor: not-allowed;"' : ''}>
                <svg width="14" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.6875 7H12.3125" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M7.5 2.1875V11.8125" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
          ` : ''}
        </div>
      </div>
    `;
  }

  renderCartAddonCard(product) {
    const quantity = product.quantity || 1;
    // Stored price is always dollars regardless of priceSource — see loadCartAddons.
    const price = product.variants && product.variants[0] ? parseFloat(product.variants[0].price) : 0;
    const variantId = product.variants && product.variants[0] ? product.variants[0].id : null;

    const resizedImage = this.getResizedImageUrl(product.image, '120x120');
    const addonMinusAria =
      quantity === 1 ? `Remove ${product.title} from cart` : 'Decrease quantity';
    const addonMinusAriaSafe = addonMinusAria.replace(/&/g, '&amp;').replace(/"/g, '&quot;');

    return `
      <div class="cart-item custom-box-preview-modal__line" data-product-id="${product.id}" ${variantId ? `data-variant-id="${variantId}"` : ''}>
        <div class="cart-item__product flex items-center gap-2">
          <div class="cart-item__media blocks-radius-sm media-wrapper custom-box-preview-modal__thumb">
            ${resizedImage
              ? `<img src="${resizedImage}" alt="${product.title}" loading="lazy" width="120" height="120" style="width: 100%; height: 100%; object-fit: cover; display: block;">`
              : WG_BUNDLE_IMAGE_PLACEHOLDER}
          </div>
          <div class="cart-item__details flex-grow flex flex-col gap-1 min-w-0">
            <div class="grid min-w-0">
              <div class="block min-w-0">
                <span class="cart-item__title text-pcard-title">${product.title}</span>
              </div>
              <div class="cart-item__options">
                <span class="cart-item__option-value text-sm text-subtext">Add-on • $${price.toFixed(2)}</span>
              </div>
            </div>
          </div>
          <div class="cart-item__action flex flex-row items-center justify-end gap-2 flex-shrink-0">
            <div class="cart-quantity quantity">
              <button type="button" name="minus" class="quantity__button ${quantity === 1 ? 'qty-stepper-remove' : ''}" data-cart-action="decrease" data-variant-id="${variantId}" aria-label="${addonMinusAriaSafe}">
                ${quantity === 1 ? WG_BUNDLE_TRASH_SVG : WG_BUNDLE_MINUS_SVG}
              </button>
              <input class="quantity__input" type="number" value="${quantity}" readonly style="pointer-events: none;">
              <button type="button" name="plus" class="quantity__button" data-cart-action="increase" data-variant-id="${variantId}" aria-label="Increase quantity">
                <svg width="14" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.6875 7H12.3125" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M7.5 2.1875V11.8125" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  attachCartAddonListeners() {
    const buttons = this.querySelectorAll('[data-cart-action]');
    buttons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const action = button.dataset.cartAction;
        const variantId = button.dataset.variantId;
        if (!variantId) return;

        // Find current quantity from our rendered data
        const addon = (this.cartAddons || []).find(a => a.variants && String(a.variants[0].id) === String(variantId));
        const currentQty = addon ? (addon.quantity || 0) : 0;

        if (action === 'increase') {
          this.changeCartQuantity(variantId, currentQty + 1);
        } else if (action === 'decrease') {
          const newQty = Math.max(0, currentQty - 1);
          this.changeCartQuantity(variantId, newQty);
        }
      });
    });
  }

  attachProductListeners() {
    // Use event delegation to avoid duplicate listeners
    // Only attach once - the listener will handle all current and future buttons
    if (this._productListenerAttached) {
      return;
    }

    this.productsGrid.addEventListener('click', (e) => {
      const actionButton = e.target.closest('[data-action]');
      if (actionButton) {
        e.preventDefault();
        e.stopPropagation();
        const action = actionButton.dataset.action;
        const productId = actionButton.dataset.productId;

        this.handleQuantityChange(productId, action);
        return;
      }
      // Portal tray extras: dashed "Add an item" slots and Clear box.
      const addSlot = e.target.closest('[data-wg-add-item]');
      if (addSlot) {
        e.preventDefault();
        this.handlePortalAddItem();
        return;
      }
      const clearBtn = e.target.closest('[data-wg-clear-box]');
      if (clearBtn) {
        e.preventDefault();
        this.handlePortalClearBox(clearBtn);
      }
    });

    this._productListenerAttached = true;
  }

  handleRemove(productId) {
    try {
      const boxStateStr = localStorage.getItem('wildgrain-custom-box-state');
      if (!boxStateStr) return;

      const boxState = JSON.parse(boxStateStr);
      delete boxState.productQuantities[productId];

      // Update boxFull flag after removal
      const totalItems = Object.values(boxState.productQuantities || {}).reduce((sum, qty) => sum + qty, 0);
      const boxSize = this.getBoxSize(boxState.selectedSize);
      boxState.boxFull = totalItems >= boxSize;

      // Save updated state
      localStorage.setItem('wildgrain-custom-box-state', JSON.stringify(boxState));

      // Dispatch custom event to update status bar with the updated box state
      document.dispatchEvent(new CustomEvent('custom-box-state:updated', { detail: boxState }));
      window.dispatchEvent(new CustomEvent('customBoxStateChanged', { detail: boxState }));

      // Reload box state and re-render
      this.boxState = boxState;
      this.renderProducts();

      // If no items left, close modal
      if (Object.keys(boxState.productQuantities).length === 0) {
        this.close();
      }
    } catch (error) {
      // Error removing product
    }
  }

  handleQuantityChange(productId, action) {
    if (action !== 'increase' && action !== 'decrease') return;
    try {
      const boxStateStr = localStorage.getItem('wildgrain-custom-box-state');
      if (!boxStateStr) return;

      const boxState = JSON.parse(boxStateStr);
      const currentQty = boxState.productQuantities[productId] || 0;

      if (action === 'increase') {
        // Check if box is full using the boxFull flag
        if (boxState.boxFull === true) {
          return;
        }

        // Also check total items vs box size as a fallback
        const totalItems = Object.values(boxState.productQuantities || {}).reduce((sum, qty) => sum + qty, 0);
        const boxSize = this.getBoxSize(boxState.selectedSize);

        if (totalItems >= boxSize) {
          // Update boxFull flag
          boxState.boxFull = true;
          localStorage.setItem('wildgrain-custom-box-state', JSON.stringify(boxState));
          window.dispatchEvent(new CustomEvent('customBoxStateChanged', { detail: boxState }));
          return;
        }

        boxState.productQuantities[productId] = currentQty + 1;

        // Update boxFull flag based on new total
        const newTotal = Object.values(boxState.productQuantities).reduce((sum, qty) => sum + qty, 0);
        boxState.boxFull = newTotal >= boxSize;
      } else if (action === 'decrease') {
        const newQty = currentQty - 1;
        if (newQty <= 0) {
          delete boxState.productQuantities[productId];
        } else {
          boxState.productQuantities[productId] = newQty;
        }

        // Update boxFull flag - if we're decreasing, box can't be full
        const totalItems = Object.values(boxState.productQuantities || {}).reduce((sum, qty) => sum + qty, 0);
        const boxSize = this.getBoxSize(boxState.selectedSize);
        boxState.boxFull = totalItems >= boxSize;
      }

      // Save updated state
      localStorage.setItem('wildgrain-custom-box-state', JSON.stringify(boxState));

      // Dispatch custom event to update status bar with the updated box state
      document.dispatchEvent(new CustomEvent('custom-box-state:updated', { detail: boxState }));
      window.dispatchEvent(new CustomEvent('customBoxStateChanged', { detail: boxState }));

      // Update box state
      this.boxState = boxState;

      if (this.isPortalMode) {
        // Portal tray layout shifts with every change (dashed slots appear/
        // disappear, stepper icons flip) — re-render the whole body.
        this.renderProducts();
      } else {
        // Update only the affected quantity input to avoid flicker
        this.updateQuantityInput(productId, boxState.productQuantities[productId] || 0);

        // Update header info (totals, prices, etc.)
        this.updateHeaderInfo();
      }

      // If no items left, close modal
      if (Object.keys(boxState.productQuantities).length === 0) {
        this.close();
      }
    } catch (error) {
      // Error updating quantity
    }
  }

  updateQuantityInput(productId, newQuantity) {
    // Find the quantity input for this product (not the free item card)
    const productCard = this.querySelector(`[data-product-id="${productId}"]:not(.custom-box-preview-modal__line--free)`);
    if (productCard) {
      // For free products, display the paid quantity (raw - 1)
      const isFreeProduct = this.boxState?.freeProduct && String(productId) === String(this.boxState.freeProduct);
      const displayQty = isFreeProduct ? Math.max(0, newQuantity - 1) : newQuantity;

      const quantityInput = productCard.querySelector('.quantity__input');
      if (quantityInput) {
        quantityInput.value = displayQty;
      }

      const minusBtn = productCard.querySelector('.quantity__button[name="minus"]');
      if (minusBtn && displayQty > 0) {
        const title = productCard.querySelector('.cart-item__title')?.textContent?.trim() || 'item';
        if (displayQty === 1) {
          minusBtn.innerHTML = WG_BUNDLE_TRASH_SVG;
          minusBtn.classList.add('qty-stepper-remove');
          minusBtn.setAttribute('aria-label', `Remove ${title} from box`);
        } else {
          minusBtn.innerHTML = WG_BUNDLE_MINUS_SVG;
          minusBtn.classList.remove('qty-stepper-remove');
          minusBtn.setAttribute('aria-label', `Decrease ${title} quantity`);
        }
      }

      // Remove the card if quantity is 0, or if it's the free product and only the free unit remains
      if (newQuantity === 0 || (isFreeProduct && newQuantity <= 1)) {
        productCard.remove();
      }
    }

    // Update button states
    const boxState = this.boxState;
    const freeProductId = boxState?.freeProduct;
    let totalItems = 0;
    Object.entries(boxState?.productQuantities || {}).forEach(([productId, qty]) => {
      totalItems += qty;
    });
    const boxSize = this.getBoxSize(boxState?.selectedSize);
    const isBoxFull = totalItems >= boxSize;

    // Update all increase buttons based on box full state
    this.querySelectorAll('[data-action="increase"]').forEach(btn => {
      if (isBoxFull) {
        btn.disabled = true;
        btn.style.opacity = '0.5';
        btn.style.cursor = 'not-allowed';
      } else {
        btn.disabled = false;
        btn.style.opacity = '1';
        btn.style.cursor = 'pointer';
      }
    });
  }

  updateHeaderInfo() {
    const boxState = this.boxState;
    if (!boxState) return;

    const productQuantities = boxState.productQuantities || {};
    const freeProductId = boxState?.freeProduct;
    let totalItems = 0;
    Object.entries(productQuantities).forEach(([productId, qty]) => {
      totalItems += qty;
    });
    const boxSize = this.getBoxSize(boxState.selectedSize);

    // Update footer button
    this.updateFooterButton(totalItems >= boxSize);

    // Update header using shared method
    const headerInfo = this.querySelector('[data-modal-header-info]');
    if (headerInfo) {
      headerInfo.innerHTML = this.generateHeaderHTML();
    }
  }

  /**
   * Binds click handler to the upgrade box size link
   */
  bindUpgradeLink(container) {
    const upgradeLink = container.querySelector('.upgrade-box-size-link');
    if (upgradeLink) {
      upgradeLink.addEventListener('click', (e) => {
        e.preventDefault();
        // Close the modal
        this.close();
        
        // Navigate to the bundle product page to change box size
        const bundleHandle = this.boxState?.bundleHandle;
        if (bundleHandle) {
          window.location.href = `/products/${bundleHandle}`;
        } else {
          // Fallback to custom-wildgrain-box
          window.location.href = '/products/custom-wildgrain-box';
        }
      });
    }
  }

  getBoxSize(selectedSize) {
    if (!selectedSize) return 7;
    const match = selectedSize.match(/(\d+)/);
    return match ? parseInt(match[1]) : 7;
  }

  /** Items still required to reach plan capacity (same basis as header “Add X more items”). */
  getItemsNeededToCompleteBox() {
    const pq = this.boxState?.productQuantities || {};
    const freeProductId = this.boxState?.freeProduct;
    let total = 0;
    Object.entries(pq).forEach(([productId, qty]) => {
      total += qty;
    });
    const boxSize = this.getBoxSize(this.boxState?.selectedSize);
    return Math.max(0, boxSize - total);
  }

  /** Footer CTA when user is still building on the product page (not full). */
  getKeepBuildingFooterLabel() {
    const n = this.getItemsNeededToCompleteBox();
    if (this.isPortalMode) {
      // App tray copy: "Add N more items".
      if (n <= 0) return 'Close';
      return n === 1 ? 'Add 1 more item' : `Add ${n} more items`;
    }
    if (n <= 0) return 'CLOSE';
    return n === 1 ? '1 MORE ITEM NEEDED' : `${n} MORE ITEMS NEEDED`;
  }

  /**
   * True only on the bundle PDP that matches saved box state — NOT merely because
   * #wildgrain-box-builder-root exists in the footer on every page.
   */
  isOnBundleCustomizeProductPage() {
    const path = window.location.pathname || '';
    if (!path.includes('/products/')) return false;
    const bundleHandle = (this.boxState && this.boxState.bundleHandle) || 'custom-wildgrain-box';
    const escaped = String(bundleHandle).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`/products/${escaped}(?:/|$)`, 'i').test(path);
  }

  /**
   * Same destination as “Customize Your Box”: bundle product URL with plan/size query params
   * (BoxBuilder reads these on the recharge-bundle / bundle PDP).
   * Portal mode goes to the PDP too — the portal builder renders there, so
   * every theme CTA (keep building, review & add extras, plan settings)
   * lands in one consistent place.
   */
  buildBundleCustomizeUrl() {
    const bundleHandle = (this.boxState && this.boxState.bundleHandle) || 'custom-wildgrain-box';
    const url = new URL(`/products/${bundleHandle}`, window.location.origin);
    const size = this.boxState && this.boxState.selectedSize;
    const plan = this.boxState && this.boxState.selectedPlan;
    if (size) url.searchParams.set('selectedSize', size);
    if (plan) url.searchParams.set('selectedPlan', plan);
    return url.pathname + url.search;
  }

  /**
   * Compact row under “PLAN ITEMS” when box is full and user is off the bundle PDP.
   */
  getPlanItemsExploreLinkHTML() {
    if (this.isOnBundleCustomizeProductPage()) return '';
    const href = this.escapeHtml(this.buildBundleCustomizeUrl());
    return `
      <div class="custom-box-preview-modal__plan-items-explore">
        <a
          href="${href}"
          class="custom-box-preview-modal__plan-items-explore-link"
          aria-label="Go to the box product page to explore plans and sizes"
        >
          <span class="custom-box-preview-modal__plan-items-explore-copy">
            <span class="custom-box-preview-modal__plan-items-explore-title">More plans &amp; sizes</span>
            <span class="custom-box-preview-modal__plan-items-explore-sub">${this.isPortalMode ? 'Explore options in the box builder' : 'Explore options on the product page'}</span>
          </span>
          <span class="custom-box-preview-modal__plan-items-explore-arrow" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M9 5l7 7-7 7" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </a>
      </div>
    `;
  }

  /**
   * Shared pricing / progress snapshot for header + sticky footer summary.
   */
  getModalPricingSnapshot() {
    const boxState = this.boxState;
    if (!boxState) return null;

    const productQuantities = boxState.productQuantities || {};
    const selectedSize = boxState.selectedSize || '7 items';
    const freeProductId = boxState?.freeProduct;

    let totalItems = 0;
    const freeItemCount = freeProductId ? 1 : 0;
    Object.entries(productQuantities).forEach(([productId, qty]) => {
      totalItems += qty;
    });

    const boxSize = this.getBoxSize(selectedSize);
    const totalPrice = this.calculateTotalPrice(productQuantities);
    const displayCapacity = boxSize + freeItemCount;
    const addOnsCount = Math.max(0, totalItems - boxSize);
    const totalItemsWithFree = totalItems + freeItemCount;

    const variantTitle = selectedSize || '';
    const is4ItemsVariant = variantTitle.toLowerCase().includes('4 items');

    let discount = boxState.discount;
    let hasDiscount = !is4ItemsVariant && discount && discount.amount > 0;
    let discountAmount = hasDiscount ? parseFloat(discount.amount) : 0;

    let hasBlockedDiscount = false;

    if (!hasDiscount && !is4ItemsVariant) {
      const autoDiscount = this.getCookie('wg_auto_discount');
      if (autoDiscount) {
        const raw = this.getCookie('wg_auto_discount_amount');
        const parsed = raw != null && raw !== '' ? parseFloat(raw) : NaN;
        const amt = Number.isFinite(parsed) && parsed > 0 ? parsed : 20;
        discount = { title: autoDiscount, amount: amt };
        hasDiscount = true;
        discountAmount = amt;
      }
    }

    if (is4ItemsVariant) {
      const potentialDiscount = boxState.discount || {};
      const autoDiscountCookie = this.getCookie('wg_auto_discount');
      if ((potentialDiscount.amount && potentialDiscount.amount > 0) || autoDiscountCookie) {
        hasBlockedDiscount = true;
      }
    }

    // Friend-offer: box is 100% off via price rule — show full price as discount
    const isFO = (function() { try { return sessionStorage.getItem('wg_friend_offer') === 'true'; } catch(e) { return false; } })();
    if (isFO && totalPrice > 0) {
      discount = { title: 'First Box Free', amount: totalPrice };
      hasDiscount = true;
      discountAmount = totalPrice;
    }

    const finalPrice = totalPrice - discountAmount;
    const showCompareAt =
      hasDiscount && parseFloat(totalPrice) > parseFloat(finalPrice) + 0.005;
    const priceDisplay = hasDiscount ? finalPrice : totalPrice;
    const isComplete = totalItems >= boxSize;

    return {
      totalItems,
      freeItemCount,
      boxSize,
      addOnsCount,
      totalItemsWithFree,
      displayCapacity,
      totalPrice,
      finalPrice,
      discountAmount,
      hasDiscount,
      discount,
      showCompareAt,
      priceDisplay,
      is4ItemsVariant,
      hasBlockedDiscount,
      isComplete
    };
  }

  /**
   * Escapes text for safe use inside HTML attributes / innerHTML text nodes.
   */
  escapeHtml(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Clear-and-conspicuous automatic-renewal disclosure shown in the modal
   * footer, in visual proximity to the Add to Cart / Continue button, as
   * required by California's Automatic Renewal Law (Cal. Bus. & Prof. Code
   * Sec. 17600 et seq.). Returns '' for one-time / trial boxes so it only
   * appears for auto-renewing subscriptions.
   */
  generateSubscriptionTermsHTML() {
    const frequency = this.getFrequencyDisplayLabel();
    if (!frequency) return '';
    const freqText = this.escapeHtml(frequency.toLowerCase());
    if (this.isPortalMode) {
      // App's one-line ARL: "Renews every 30 days — free to cancel, see how."
      return `
        <p class="wg-tray-arl" role="note" aria-label="Subscription auto-renewal terms">
          Renews ${freqText} — free to cancel,
          <a href="https://wildgrain.com/policies/terms-of-service">see how</a>.
        </p>
      `;
    }
    return `
      <p class="custom-box-preview-modal__subscription-terms" role="note" aria-label="Subscription auto-renewal terms"
        style="margin: 0.5rem 0 0; font-size: 11.5px; line-height: 1.4; color: #6b6b6b; text-align: center;">
        Renews automatically and continues until you cancel. Charged ${freqText}. Cancel anytime, no fee, in your Member Account.
        <a href="https://wildgrain.com/policies/terms-of-service" style="color: inherit; text-decoration: underline; text-underline-offset: 2px;">Cancellation policy</a>.
      </p>
    `;
  }

  /**
   * Footer above CTA: discount strip first, then total row (Total: left, amount right).
   */
  generateFooterSummaryHTML() {
    const s = this.getModalPricingSnapshot();
    if (!s) return '';

    // Portal: the app tray's footer — Total row ("18% off  ~~$121~~  $99")
    // shown at every stage, over the trust row. No "N more to finish" line;
    // that story is told by the header meter and the CTA label.
    if (this.isPortalMode) {
      const money = (v) => {
        const n = parseFloat(v) || 0;
        return n % 1 === 0 ? `$${n.toFixed(0)}` : `$${n.toFixed(2)}`;
      };
      const pct = s.showCompareAt && parseFloat(s.totalPrice) > 0
        ? Math.round((parseFloat(s.discountAmount) / parseFloat(s.totalPrice)) * 100)
        : 0;
      return `
        <div class="wg-tray-total">
          <span class="wg-tray-total-label">Total</span>
          <span class="wg-tray-total-amounts">
            ${pct > 0 ? `<span class="wg-tray-off">${pct}% off</span>` : ''}
            ${s.showCompareAt ? `<s class="wg-tray-compare"><span class="visually-hidden">Original price </span>${money(s.totalPrice)}</s>` : ''}
            <span class="wg-tray-final">${money(s.priceDisplay)}</span>
          </span>
        </div>
        <div class="wg-tray-trust">
          <span class="wg-tray-trust-item">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-8.5c-.382-.54-1.007-.876-1.67-.876H14.25m-10.5 0H12v6.75m-9.75-6.75V6.615c0-.62.504-1.115 1.125-1.115h7.5c.621 0 1.125.495 1.125 1.115v.51"/></svg>
            Free delivery
          </span>
          <span class="wg-tray-trust-item">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 3v18M5.636 5.636l12.728 12.728M3 12h18M5.636 18.364L18.364 5.636"/></svg>
            Arrives frozen-fresh
          </span>
          <span class="wg-tray-trust-item">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            Cancel anytime
          </span>
        </div>
      `;
    }

    const itemWord = s.totalItems === 1 ? 'item' : 'items';
    const priceStr = `$${parseFloat(s.priceDisplay).toFixed(2)}`;
    const compareStr = `$${parseFloat(s.totalPrice).toFixed(2)}`;
    const discountLabel = s.discount && s.discount.title
      ? this.escapeHtml(s.discount.title)
      : 'Discount applied';

    if (!s.isComplete) {
      const itemsNeeded = Math.max(0, s.boxSize - s.totalItems);
      const line = `${s.totalItems} of ${s.boxSize} ${itemWord} · ${priceStr} est. · ${itemsNeeded} more to finish`;
      return `<p class="custom-box-preview-modal__footer-summary-incomplete">${this.escapeHtml(line)}</p>`;
    }

    const compareBlock = s.showCompareAt
      ? `<span class="custom-box-preview-modal__footer-price-compare">
          <span class="visually-hidden">Original price&nbsp;</span>
          ${compareStr}
        </span>`
      : '';

    const totalRowClass = s.showCompareAt
      ? 'custom-box-preview-modal__footer-total-row'
      : 'custom-box-preview-modal__footer-total-row custom-box-preview-modal__footer-total-row--single';

    const totalRow = `
      <div class="${totalRowClass}">
        <span class="custom-box-preview-modal__footer-total-label">Total:</span>
        <div class="custom-box-preview-modal__footer-total-amounts">
          ${compareBlock}
          <span class="custom-box-preview-modal__footer-price-final">${priceStr}</span>
        </div>
      </div>
    `;

    const discountStrip =
      s.hasDiscount && parseFloat(s.discountAmount) > 0
        ? `
      <div class="custom-box-preview-modal__footer-discount-strip">
        <div class="custom-box-preview-modal__footer-discount-strip-start">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M2.5 8.65467L4.42267 6.732M11.5773 13.7693L13.5 11.8467M10.0773 3.232L6.92267 6.38667M13.2333 5.26667L10.7333 2.76667L2.76667 10.7333L5.26667 13.2333L13.2333 5.26667Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span class="custom-box-preview-modal__footer-discount-name">${discountLabel}</span>
        </div>
        <span class="custom-box-preview-modal__footer-discount-amount">-$${parseFloat(s.discountAmount).toFixed(2)}</span>
      </div>
    `
        : '';

    return `
      <div class="custom-box-preview-modal__footer-checkout">
        ${discountStrip}
        ${totalRow}
      </div>
    `;
  }

  /**
   * 4-item plan discount notice — lives in footer only (not header).
   */
  updateFooterDiscountNotice() {
    const el = this.querySelector('[data-footer-discount-notice]');
    if (!el) return;

    const s = this.getModalPricingSnapshot();
    if (!s || !s.hasBlockedDiscount) {
      el.hidden = true;
      el.innerHTML = '';
      return;
    }

    el.hidden = false;
    el.innerHTML = `
      <div class="custom-box-preview-modal__notice custom-box-preview-modal__notice--warning flex items-start gap-2">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="custom-box-preview-modal__notice-icon" aria-hidden="true">
          <path d="M8 5.33333V8M8 10.6667H8.00667M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span class="text-sm">Discount codes don't apply to 4-item plans. <a href="#" class="upgrade-box-size-link font-body-bold underline">Upgrade to 6+ items</a> to use your discount.</span>
      </div>
    `;
    this.bindUpgradeLink(el);
  }

  /**
   * Generates the header info HTML for the modal
   * Extracted to avoid duplication between renderProducts() and updateHeaderInfo()
   */
  generateHeaderHTML() {
    const s = this.getModalPricingSnapshot();
    if (!s) return '';

    const { totalItems, boxSize, isComplete } = s;

    // Portal: the app tray's header — "N of M items picked" over the shared
    // segmented meter (gold runway for extras once ready). No "Box ready"
    // banner, no frequency pill (renewal cadence lives in the ARL footer).
    if (this.isPortalMode) {
      const extras = Math.max(0, totalItems - boxSize);
      let segs = '';
      for (let i = 0; i < boxSize; i++) {
        segs += `<span class="wg-seg${i < totalItems ? ' is-filled' : ''}"></span>`;
      }
      if (isComplete) {
        segs += '<span class="wg-seg-divider"></span>';
        for (let i = 0; i < Math.min(extras, 8); i++) {
          segs += '<span class="wg-seg wg-seg--extra"></span>';
        }
        segs += '<span class="wg-seg wg-seg--invite"></span>';
      }
      const picked = Math.min(totalItems, boxSize);
      const extrasNote = extras > 0 ? ` + ${extras} extra${extras === 1 ? '' : 's'}` : '';
      return `
        <div class="wg-tray-progress" role="status">
          <span class="wg-tray-progress-count">${picked} of ${boxSize} items picked${extrasNote}</span>
          <span class="wg-tray-segments" aria-hidden="true">${segs}</span>
        </div>
      `;
    }

    const countLabel = isComplete
      ? `${totalItems} ${totalItems === 1 ? 'item' : 'items'}`
      : `${totalItems} of ${boxSize} items`;

    // Portal avoids "complete/full" framing — a ready box still has room for
    // extras in the builder, so don't tell the shopper they're done.
    const completePhrase = this.isPortalMode ? 'Box ready' : 'Box complete';

    const ariaSummary = isComplete
      ? `${completePhrase}, ${countLabel}`
      : `In progress, ${countLabel}`;
    const ariaSummarySafe = ariaSummary.replace(/&/g, '&amp;').replace(/"/g, '&quot;');

    const frequencyPill = this.renderFrequencyInlineHTML();

    const completeBlock = isComplete
      ? `
        ${WG_STATUS_CHECK_SVG}
        <span class="custom-box-preview-modal__summary-status-line">
          <span class="custom-box-preview-modal__summary-phrase">${completePhrase}</span>
          <span class="custom-box-preview-modal__summary-sep" aria-hidden="true">•</span>
          <span class="custom-box-preview-modal__summary-count">${countLabel}</span>
        </span>
        ${frequencyPill}
      `
      : `
        <span class="custom-box-preview-modal__summary-status-line">
          <span class="custom-box-preview-modal__summary-phrase">In progress</span>
          <span class="custom-box-preview-modal__summary-sep" aria-hidden="true">•</span>
          <span class="custom-box-preview-modal__summary-count">${countLabel}</span>
        </span>
        ${frequencyPill}
      `;

    return `
      <div class="custom-box-preview-modal__header-stack w-full">
        <div class="custom-box-preview-modal__summary-row ${isComplete ? 'custom-box-preview-modal__summary-row--complete' : 'custom-box-preview-modal__summary-row--progress'}" role="status" aria-label="${ariaSummarySafe}" data-complete="${isComplete ? 'true' : 'false'}">
          ${completeBlock}
        </div>
      </div>
    `;
  }

  setSaveButtonLabel(text) {
    const saveButton = this.querySelector('[data-save-button]');
    if (!saveButton) return;
    const label = saveButton.querySelector('[data-save-button-label]');
    if (label) {
      label.textContent = text;
    } else {
      saveButton.textContent = text;
    }
  }

  setSaveButtonLoading(isLoading) {
    const saveButton = this.querySelector('[data-save-button]');
    if (!saveButton) return;
    if (isLoading) {
      saveButton.disabled = true;
      saveButton.classList.add('custom-box-preview-modal__save-btn--loading');
      saveButton.setAttribute('aria-busy', 'true');
    } else {
      saveButton.disabled = false;
      saveButton.classList.remove('custom-box-preview-modal__save-btn--loading');
      saveButton.removeAttribute('aria-busy');
    }
  }

  updateFooterButton(isBoxFull) {
    const saveButton = this.querySelector('[data-save-button]');
    if (!saveButton) return;

    // Portal Beam parity with the app: the food-bank picker earns its space
    // at the checkout stage — expand on the not-full → full EDGE, collapse on
    // the way back. Edges only, so a manual toggle in between sticks.
    if (this.isPortalMode) {
      if (this.prevBoxFullForBeam === null || isBoxFull !== this.prevBoxFullForBeam) {
        this.beamOpen = isBoxFull;
        this.syncPortalBeam();
      }
      this.prevBoxFullForBeam = isBoxFull;
    }

    const summaryEl = this.querySelector('[data-footer-checkout-summary]');
    if (summaryEl) {
      summaryEl.innerHTML = this.generateFooterSummaryHTML();
    }
    this.updateFooterDiscountNotice();

    const subTermsEl = this.querySelector('[data-subscription-terms]');
    if (subTermsEl) {
      subTermsEl.innerHTML = this.generateSubscriptionTermsHTML();
    }

    const onBundleCustomizePdp = this.isOnBundleCustomizeProductPage();

    // Clear loading state from a previous add-to-cart (modal may reopen while button stayed disabled)
    this.setSaveButtonLoading(false);
    saveButton.style.opacity = '1';
    saveButton.setAttribute('data-box-complete', isBoxFull ? 'true' : 'false');

    if (isBoxFull && this.isPortalMode) {
      // Portal: never add to cart from the theme, never open the Member Deals
      // slider (retired). The portal builder owns extras + checkout, so a
      // ready box sends the visitor there to review and keep adding.
      this.setSaveButtonLabel('Review box & add extras');
      saveButton.style.fontWeight = 'bold';
      saveButton.onclick = (e) => {
        e.preventDefault();
        window.location.href = this.buildBundleCustomizeUrl();
      };
    } else if (isBoxFull) {
      const cartButtonLabel = this.hasBoxInCart ? 'CONTINUE' : 'ADD TO CART';
      this.setSaveButtonLabel(cartButtonLabel);
      saveButton.style.fontWeight = 'bold';
      saveButton.onclick = async (e) => {
        e.preventDefault();
        if (saveButton.classList.contains('custom-box-preview-modal__save-btn--loading')) {
          return;
        }

        const originalText = saveButton.querySelector('[data-save-button-label]')?.textContent || cartButtonLabel;
        this.setSaveButtonLoading(true);

        try {
          const addToCartEvent = new CustomEvent('wildgrain-box-builder:addToCart', {
            detail: { boxState: this.boxState }
          });
          window.dispatchEvent(addToCartEvent);

          const handleSuccess = () => {
            this.setSaveButtonLabel(originalText);
            this.setSaveButtonLoading(false);
            saveButton.style.opacity = '1';
            // Member Deals opens on top first; checkout-addons.js closes this drawer after it’s active
            window.removeEventListener('wildgrain-box-builder:addToCartError', handleError);
          };

          const handleError = (event) => {
            this.setSaveButtonLabel(originalText);
            this.setSaveButtonLoading(false);
            saveButton.style.opacity = '1';
            const errorAction = this.hasBoxInCart ? 'continue' : 'add to cart';
            window.dispatchEvent(new CustomEvent('wildgrain-toast', {
              detail: { message: `Failed to ${errorAction}: ${event.detail.error}. Please try again.`, type: 'error', duration: 7000 }
            }));
            window.removeEventListener('wildgrain-box-builder:addToCartSuccess', handleSuccess);
          };

          window.addEventListener('wildgrain-box-builder:addToCartSuccess', handleSuccess, { once: true });
          window.addEventListener('wildgrain-box-builder:addToCartError', handleError, { once: true });

          setTimeout(() => {
            window.removeEventListener('wildgrain-box-builder:addToCartSuccess', handleSuccess);
            window.removeEventListener('wildgrain-box-builder:addToCartError', handleError);
            if (saveButton.classList.contains('custom-box-preview-modal__save-btn--loading')) {
              this.setSaveButtonLabel(originalText);
              this.setSaveButtonLoading(false);
              saveButton.style.opacity = '1';
            }
          }, 15000);
        } catch (error) {
          this.setSaveButtonLabel(originalText);
          this.setSaveButtonLoading(false);
          saveButton.style.opacity = '1';
          const errorAction = this.hasBoxInCart ? 'continue' : 'add to cart';
          window.dispatchEvent(new CustomEvent('wildgrain-toast', {
            detail: { message: `Failed to ${errorAction}: ${error.message}. Please try again.`, type: 'error', duration: 7000 }
          }));
        }
      };
    } else if (!onBundleCustomizePdp) {
      // Not on the bundle PDP: go to bundle product with plan/size params (same as “Customize Your Box”).
      this.setSaveButtonLabel(this.getKeepBuildingFooterLabel());
      saveButton.style.fontWeight = 'bold';
      saveButton.onclick = async (e) => {
        e.preventDefault();
        const bundleHandle = this.boxState?.bundleHandle;
        const bundleId = this.boxState?.bundleId;

        if (bundleHandle) {
          window.location.href = this.buildBundleCustomizeUrl();
          return;
        }
        if (bundleId) {
          try {
            const response = await fetch(`/products/${bundleId}.js`);
            if (response.ok) {
              const product = await response.json();
              if (product.handle) {
                this.boxState = { ...this.boxState, bundleHandle: product.handle };
                window.location.href = this.buildBundleCustomizeUrl();
                return;
              }
            }
          } catch (err) {
            // fall through
          }
          window.location.href = this.buildBundleCustomizeUrl();
          return;
        }
        window.location.href = this.buildBundleCustomizeUrl();
      };
    } else {
      // On the bundle product page — dismiss and keep building in-page
      this.setSaveButtonLabel(this.getKeepBuildingFooterLabel());
      saveButton.style.fontWeight = 'bold';
      saveButton.onclick = (e) => {
        e.preventDefault();
        this.close();
      };
    }
  }

  calculateTotalPrice(productQuantities) {
    // Use selectedPlanPrice from box state instead of individual item prices
    const planPrice = parseFloat(this.boxState?.selectedPlanPrice) || 0;

    // Calculate addon items price (items beyond box size)
    const boxSize = this.getBoxSize(this.boxState?.selectedSize);
    const freeProductId = this.boxState?.freeProduct;

    let totalItems = 0;
    Object.entries(productQuantities || {}).forEach(([productId, qty]) => {
      totalItems += qty;
    });

    let addonPrice = 0;
    if (totalItems > boxSize) {
      let itemsProcessed = 0;
      Object.entries(productQuantities).forEach(([productId, quantity]) => {
        if (freeProductId && String(productId) === String(freeProductId)) return;

        const product = this.products.find(p => String(p.id) === String(productId));
        if (product && product.variants && product.variants[0]) {
          const price = parseFloat(product.variants[0].price) || 0;

          // Determine how many of this product's quantity are addons
          const itemsAfterThisProduct = itemsProcessed + quantity;

          if (itemsAfterThisProduct > boxSize) {
            // Some or all of these items are addons
            const addonQuantity = Math.min(quantity, itemsAfterThisProduct - boxSize);
            const thisProductAddonPrice = price * addonQuantity;
            addonPrice += thisProductAddonPrice;
          }

          itemsProcessed += quantity;
        }
      });
    }

    // Add cart addons to the total price (using same method as box products)
    let cartAddonsPrice = 0;
    if (this.cartAddons && this.cartAddons.length > 0) {
      this.cartAddons.forEach(addon => {
        if (addon.variants && addon.variants[0]) {
          const quantity = addon.quantity || 1;
          // Stored price is always dollars regardless of priceSource — see loadCartAddons.
          const price = parseFloat(addon.variants[0].price) || 0;
          cartAddonsPrice += price * quantity;
        }
      });
    }

    const finalPrice = (totalItems > boxSize ? planPrice + addonPrice : planPrice) + cartAddonsPrice;

    return finalPrice;
  }

  attachEventListeners() {
    this.overlay = this.querySelector('[data-overlay]');
    this.closeButton = this.querySelector('[data-close-button]');
    this.productsGrid = this.querySelector('[data-products-grid]');
    const drawerInner = this.querySelector('.drawer__inner');

    // Click handler on the component itself (backdrop)
    this.addEventListener('click', (e) => {
      // Only close if clicking directly on the component (not on drawer content)
      if (e.target === this) {
        this.close();
      }
    });

    if (this.overlay) {
      this.overlay.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      });
    }

    // Prevent clicks inside drawer from bubbling up
    if (drawerInner) {
      drawerInner.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }

    if (this.closeButton) {
      this.closeButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    }

    // Note: saveButton click handler is now managed dynamically in updateFooterButton()
  }

  render() {
    this.innerHTML = `
      <div class="fixed-overlay" data-overlay aria-controls="CustomBoxPreviewModal"></div>
      <div class="drawer__inner">
        <button class="drawer__close-btn z-1" data-close-button type="button" aria-label="Close" aria-controls="CustomBoxPreviewModal">
          <svg class="icon icon--large" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M18 6L6 18M6 6l12 12" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <div class="drawer__content flex flex-col h-full w-full">
          <div class="drawer__header cart-drawer__header custom-box-preview-modal__header-bar flex flex-col items-start">
            <h2 class="drawer__heading h3 flex items-center gap-3 idler">
              BOX SELECTION
            </h2>
            <div data-modal-header-info class="w-full"></div>
            <div data-recipient-info class="w-full"></div>
          </div>
          <div class="drawer__body flex-grow v-scrollable" data-products-grid>
            <!-- Products will be rendered here -->
          </div>
          <div class="drawer__footer cart-drawer__footer custom-box-preview-modal__footer">
            <div class="custom-box-preview-modal__footer-inner">
              <div id="custom-box-preview-modal-checkout-summary" class="custom-box-preview-modal__footer-summary" data-footer-checkout-summary aria-live="polite"></div>
              <div data-footer-discount-notice class="custom-box-preview-modal__footer-discount-notice" hidden></div>
              <div class="drawer__footer-buttons flex flex-col gap-2">
                <button class="btn flex-grow custom-box-preview-modal__save-btn" data-save-button data-box-complete="false" type="button" aria-describedby="custom-box-preview-modal-checkout-summary">
                  <span class="custom-box-preview-modal__save-btn-spinner" aria-hidden="true"></span>
                  <span class="custom-box-preview-modal__save-btn-label" data-save-button-label>Close</span>
                </button>
              </div>
              <div data-subscription-terms></div>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('custom-box-preview-modal', CustomBoxPreviewModal);
